import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DownloadingOutlinedIcon from '@mui/icons-material/DownloadingOutlined';
import { pdfjs } from 'react-pdf';

import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';

import {
    PageViewportProps,
    Redacts,
    Vertices,
    inferRedact,
} from 'shared/shim/react-pdf/PageCanvas';
import DocumentRedact, {
    PDFDocumentProxyWithLoadStep,
} from '../../../../../shared/shim/react-pdf/Document';
import Document from '../../../../../shared/shim/react-pdf/PreDocument';
import Page from '../../../../../shared/shim/react-pdf/OriPage';
import PageRedact from '../../../../../shared/shim/react-pdf/Page';
import * as THREE from 'three';

import { getClientCoords } from 'shared/shim/react-pdf/helpers/selectionBox';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';

import { PDFDocument, rgb } from 'pdf-lib';
import FileSaver from 'file-saver';
import {
    PDFPrintService,
    createPrintService,
    saveOverlayManager,
} from 'shared/shim/react-pdf/helpers/pdf_print_service.js';
import { OverlayManager } from 'shared/shim/react-pdf/helpers/overlay_manager.js';
import {
    useEvent,
    useWindowSize,
    useQueue,
    useUpdateEffect,
    useHarmonicIntervalFn,
} from 'react-use';
import type { PDFPageProxy } from 'pdfjs-dist';
import type { HoistPDFDocumentProxy as PDFDocumentProxy } from 'shared/shim/react-pdf/LinkService';
import CropFreeIcon from '@mui/icons-material/CropFree';
import { DocumentMeta, LayoutOffset } from '../SolicitorDocumentPage';
import { useSelector, useDispatch } from 'react-redux';

import { CarryAppActions } from 'state/carriesapplication/Action';

const { getDocument } = pdfjs;

export type PageCallback = PDFPageProxy & {
    width: number;
    height: number;
    originalWidth: number;
    originalHeight: number;
};

const DocumentViewer: React.FC<DocumentViewerProps> = React.forwardRef(
    (props, ref) => {
        const {
            networkErrorLeft,
            networkErrorRight,
            delayedLeft,
            delayedRight,
            stack,
            type,
            step,
            result,
            isHybrid,
            isLeftActive,
            isRightActive,
            leftCategory,
            rightCategory,
            leftDocumentUri: _leftDocumentUri,
            rightDocumentUri: _rightDocumentUri,
            leftFilename,
            rightFilename,
            rightDrawn,
            leftDrawn,
            leftVerticesData,
            rightVerticesData,
            leftCompareValue,
            rightCompareValue,
            footerCategory,
            setLeftVerticesData,
            setRightVerticesData,
            handleHiddenFooter,
            handleShowFooter,
            handleNetworkError,
            emptyLeftVerticesData,
            emptyRightVerticesData,
            isLeftOriginalMissing,
            isRightOriginalMissing,
        } = props;

        // const navigation = useNavigate();
        const dispatch = useDispatch();
        const isPaperDocument = React.useMemo(
            () => isHybrid && result === 'Paper Document',
            [isHybrid, result]
        );
        const scrollLeftDocumentInputRef = React.useRef<HTMLDivElement | null>(
            null
        );
        const scrollRightDocumentInputRef = React.useRef<HTMLDivElement | null>(
            null
        );
        const [scrollingLeftDocument, setScrollingLeftDocument] =
            useState<boolean>(false);
        const [scrollingRightDocument, setScrollingRightDocument] =
            useState<boolean>(false);
        const theme = useTheme();
        const DocumentMetaProps = React.useContext(DocumentMeta);
        const matches = useMediaQuery(theme.breakpoints.up('md'));
        const [numPagesLeft, setNumPagesLeft] = React.useState<number | null>(
            null
        );
        const [numPagesRight, setNumPagesRight] = React.useState<number | null>(
            null
        );

        const [isLeftDocumentPrefetch, setLeftDocumentPrefetch] =
            useState<boolean>(true);
        const [isRightDocumentPrefetch, setRightDocumentPrefetch] =
            useState<boolean>(true);
        const [isSuccessLoadedLeft, setDocumentLeftSuccess] =
            React.useState<boolean>(false);
        const [isSuccessLoadedRight, setDocumentRightSuccess] =
            React.useState<boolean>(false);
        const [isNetworkErrorLeft, setDocumentLeftNetworkError] =
            React.useState<boolean>(false);
        const [isNetworkErrorRight, setDocumentRightNetworkError] =
            React.useState<boolean>(false);
        const [isLoaderLeft, setDocumentLoaderLeft] =
            React.useState<boolean>(false);
        const [isLoaderRight, setDocumentLoaderRight] =
            React.useState<boolean>(false);
        const [documentLeftKey, setDocumentLeftKey] = React.useState<number>(0);
        const [documentRightKey, setDocumentRightKey] =
            React.useState<number>(0);
        const [isErrorLoadedLeft, setDocumentLeftError] =
            React.useState<boolean>(false);
        const [isErrorLoadedRight, setDocumentRightError] =
            React.useState<boolean>(false);
        const [isSourceErrorLeft, setDocumentLeftSourceError] =
            React.useState<boolean>(false);
        const [isSourceErrorRight, setDocumentRightSourceError] =
            React.useState<boolean>(false);

        const [isDocumentSourceFinish, setDocumentSourceFinish] =
            React.useState<boolean>(false);

        const [
            isDocumentSourceLeftFinishBounced,
            setDocumentSourceLeftFinishBounced,
        ] = React.useState<boolean>(false);
        const [
            isDocumentSourceRightFinishBounced,
            setDocumentSourceRightFinishBounced,
        ] = React.useState<boolean>(false);
        const [isTimeoutScrollingFirst, setTimeOutScrollingFirst] =
            useState<boolean>(false);
        const [isTimeoutScrollingSecond, setTimeOutScrollingSecond] =
            useState<boolean>(false);
        const [isTimeoutScroll, setTimeOutScroll] = useState<boolean>(false);
        useEffect(() => {
            if (isDocumentSourceFinish === false) {
                setDocumentSourceRightFinishBounced(false);
                setDocumentSourceLeftFinishBounced(false);
            }
        }, [isDocumentSourceFinish]);
        useHarmonicIntervalFn(
            () => {
                setDocumentSourceRightFinishBounced(true);
                setDocumentSourceLeftFinishBounced(true);
            },
            isDocumentSourceFinish ? 2000 : null
        );

        const innerLeftDocumentRef = React.useRef<DocumentPDF | null>(null);
        const innerRightDocumentRef = React.useRef<DocumentPDF | null>(null);
        const outerLeftDocumentRef = React.useRef<any>(null);
        const outerRightDocumentRef = React.useRef<any>(null);

        const isLoaderLeftNeedValidation = useMemo(
            () =>
                !!(
                    isSuccessLoadedLeft === false &&
                    isLoaderLeft &&
                    documentLeftKey >= 0 &&
                    documentLeftKey < 5
                ),
            [documentLeftKey, isLoaderLeft, isSuccessLoadedLeft]
        );
        const isLoaderRightNeedValidation = useMemo(
            () =>
                !!(
                    isSuccessLoadedRight === false &&
                    isLoaderRight &&
                    documentRightKey >= 0 &&
                    documentRightKey < 5
                ),
            [documentRightKey, isLoaderRight, isSuccessLoadedRight]
        );

        useLayoutEffect(() => {
            if (isErrorLoadedLeft) {
                if (documentLeftKey < 5 && isNetworkErrorLeft === false) {
                    setDocumentLeftError(false);
                    setDocumentLeftKey((prev) => prev + 1);
                } else {
                    setDocumentLoaderLeft(false);
                }
            }
        }, [isErrorLoadedLeft]);
        useLayoutEffect(() => {
            if (isErrorLoadedRight) {
                if (documentRightKey < 5 && isNetworkErrorRight === false) {
                    setDocumentRightError(false);
                    setDocumentRightKey((prev) => prev + 1);
                } else {
                    setDocumentLoaderRight(false);
                }
            }
        }, [isErrorLoadedRight]);

        const isCanDrawnFromType = React.useMemo(() => type !== 'show', [type]);

        const isSignCheck = React.useMemo(() => type === 'sign-check', [type]);

        const isLeftCoverLetter = React.useMemo(
            () =>
                isCanDrawnFromType === false && leftCategory === 'cover letter',
            [isCanDrawnFromType, leftCategory]
        );

        const isRightCoverLetter = React.useMemo(
            () =>
                isCanDrawnFromType === false &&
                rightCategory === 'cover letter',
            [isCanDrawnFromType, rightCategory]
        );

        const {
            add: addScroll,
            remove: removeScroll,
            first: firstScroll,
            last: lastScroll,
            size: sizeScroll,
        } = useQueue();
        const {
            // add: addVerticesScroll,
            remove: removeVerticesScroll,
            first: firstVerticesScroll,
            last: lastVerticesScroll,
            size: sizeVerticesScroll,
        } = useQueue();
        const onDocumentLeftLoadSuccess = React.useCallback(
            ({
                numPages,
                step: docStep,
            }: PDFDocumentProxyWithLoadStep): void => {
                const { current: canvasInnerLeft } = innerLeftDocumentRef;

                if (docStep === step) {
                    setNumPagesLeft(numPages);

                    setDocumentLeftSuccess(true);
                } else {
                    canvasInnerLeft?.resetSource();
                    setDocumentLeftSuccess(false);
                }
            },
            [step]
        );

        const onDocumentRightLoadSuccess = React.useCallback(
            ({
                numPages,
                step: docStep,
            }: PDFDocumentProxyWithLoadStep): void => {
                const { current: canvasInnerRight } = innerRightDocumentRef;
                if (docStep === step) {
                    setNumPagesRight(numPages);

                    setDocumentRightSuccess(true);
                } else {
                    canvasInnerRight?.resetSource();
                    setDocumentRightSuccess(false);
                }
            },
            [step]
        );

        const [leftViewOpen, setLeftViewOpen] = React.useState<boolean>(false);
        const [rightViewOpen, setRightViewOpen] =
            React.useState<boolean>(false);
        const innerLeftTooltip = useRef<HTMLDivElement | null>(null);
        const innerRightTooltip = useRef<HTMLDivElement | null>(null);
        // const [leftViewerPage, setLeftViewerPage] = React.useState<number>(1);
        // const [, setRightViewerPage] = React.useState<number>(1);

        const redactLeftContentRef = React.useRef<inferRedact[]>([]);
        const canvasLeftContentRef = React.useRef<HTMLCanvasElement[]>([]);

        const redactRightContentRef = React.useRef<inferRedact[]>([]);
        const canvasRightContentRef = React.useRef<HTMLCanvasElement[]>([]);

        const canvasInnerLeftContentRef = React.useRef<HTMLCanvasElement[]>([]);
        const canvasInnerRightContentRef = React.useRef<HTMLCanvasElement[]>(
            []
        );

        useLayoutEffect(() => {
            if (networkErrorLeft) {
                setDocumentLeftNetworkError(true);
            }
        }, [networkErrorLeft]);
        useEffect(() => {
            if (
                isLeftDocumentPrefetch === false &&
                networkErrorLeft === false
            ) {
                setDocumentLeftNetworkError(false);
            }
        }, [isLeftDocumentPrefetch]);
        useEffect(() => {
            if (
                isRightDocumentPrefetch === false &&
                networkErrorRight === false
            ) {
                setDocumentRightNetworkError(false);
            }
        }, [isRightDocumentPrefetch]);
        useLayoutEffect(() => {
            if (networkErrorRight) {
                setDocumentRightNetworkError(true);
            }
        }, [networkErrorRight]);

        useEffect(() => {
            if (isErrorLoadedLeft === false) {
                setDocumentLeftNetworkError(false);
            }
        }, [isErrorLoadedLeft]);
        useEffect(() => {
            if (isErrorLoadedRight === false) {
                setDocumentRightNetworkError(false);
            }
        }, [isErrorLoadedRight]);

        useLayoutEffect(() => {
            if (
                (networkErrorLeft === false && isNetworkErrorLeft) ||
                (networkErrorRight === false && isNetworkErrorRight)
            ) {
                if (typeof handleNetworkError === 'function') {
                    handleNetworkError();
                }
            }
        }, [isNetworkErrorLeft, isNetworkErrorRight]);

        const documentLeftError = React.useMemo(() => {
            if (isNetworkErrorLeft) {
                return 'No internet connection, Please try again!';
            }
            if (!isPaperDocument) {
                if (isLeftOriginalMissing) {
                    return 'Document Missing.';
                }
                return 'Document Missing';
            }

            return 'Paper Document';
        }, [isLeftOriginalMissing, isNetworkErrorLeft, isPaperDocument]);
        const documentRightError = React.useMemo(() => {
            if (isNetworkErrorRight) {
                return 'No internet connection, Please try again!';
            }
            if (!isPaperDocument) {
                if (isRightOriginalMissing) {
                    return 'Document Missing.';
                }
                return 'Document Missing';
            }

            return 'Paper Document';
        }, [isNetworkErrorRight, isPaperDocument, isRightOriginalMissing]);
        const onHandleLeftSidewayToggle = React.useCallback(() => {
            setLeftViewOpen(!leftViewOpen);
        }, [leftViewOpen]);
        const onHandleRightSidewayToggle = React.useCallback(() => {
            setRightViewOpen(!rightViewOpen);
        }, [rightViewOpen]);

        /**
         * Handle left sideview element when trigger listener click.
         * @param {MouseEvent} event - event
         * @param {false | PageCallback | undefined} page - page data
         */
        const onHandleLeftSideview = React.useCallback(
            (event: MouseEvent, page: false | PageCallback | undefined) => {
                if (
                    page &&
                    canvasInnerLeftContentRef.current.some(
                        (canvasRef, index) =>
                            typeof canvasRef !== 'undefined' &&
                            index === page.pageNumber - 1
                    )
                ) {
                    canvasInnerLeftContentRef.current[
                        page.pageNumber - 1
                    ].scrollIntoView({ behavior: 'smooth' });
                }
            },
            []
        );
        /**
         * Handle right sideview element when trigger listener click.
         * @param {MouseEvent} event - event
         * @param {false | PageCallback | undefined} page - page data
         */
        const onHandleRightSideview = React.useCallback(
            (event: MouseEvent, page: false | PageCallback | undefined) => {
                if (
                    page &&
                    canvasInnerRightContentRef.current.some(
                        (canvasRef, index) =>
                            typeof canvasRef !== 'undefined' &&
                            index === page.pageNumber - 1
                    )
                ) {
                    canvasInnerRightContentRef.current[
                        page.pageNumber - 1
                    ].scrollIntoView({ behavior: 'smooth' });
                }
            },
            []
        );
        /**
         * get page element based on zoom.
         * @param {ParamDataTypeHere} scale - scale to zooom the page
         * @param {Redacts[]} arrayRedacts - current saved vertices value
         * @return {Redacts[]} changes vertices value.
         */
        const GetPageRedactBasedOnZoom = React.useCallback(
            (scale: number, arrayRedacts: Redacts[]) =>
                Array.from(arrayRedacts).map((element) => ({
                    ...element,
                    startPoint: new THREE.Vector3(
                        element.startPoint.x,
                        element.startPoint.y,
                        element.startPoint.z
                    ).multiplyScalar(scale),
                    endPoint: new THREE.Vector3(
                        element.endPoint.x,
                        element.endPoint.y,
                        element.endPoint.z
                    ).multiplyScalar(scale),
                })),
            []
        );
        const [SavedLeftDocumentSize, SetSavedLeftDocumentSize] =
            React.useState<{ width: number; height: number }[]>([]);
        const [RedactLeftDocumentSaved, SetSavedRedactForLeftDocument] =
            React.useState<Redacts[][]>();

        const [zoomLeft, setZoomLeft] = React.useState<number>(20);
        /**
         * handle zoom-in left tooltip
         */
        const onHandleZoomLeftIn = React.useCallback(() => {
            const { current: documentRedact } = redactLeftContentRef;
            const { current: documentCanvas } = canvasLeftContentRef;
            if (zoomLeft < 100) {
                const newZoomLeft = zoomLeft + 5;

                const newArrayLeftPageRedacts = Array.from(documentRedact).map(
                    (element) => element.getRedactData()
                );
                const filterDocumentCanvas = documentCanvas.filter(
                    (e) => typeof e !== 'undefined'
                );
                const newArrayLeftDocumentSize = Array.from(
                    filterDocumentCanvas
                ).map((element) => ({
                    width: element.width,
                    height: element.height,
                }));
                SetSavedLeftDocumentSize(newArrayLeftDocumentSize);
                SetSavedRedactForLeftDocument(newArrayLeftPageRedacts);
                setZoomLeft(newZoomLeft);
            }
        }, [zoomLeft]);
        /**
         * handle zoom-out left tooltip
         */
        const onHandleZoomLeftOut = React.useCallback(() => {
            const { current: documentRedact } = redactLeftContentRef;
            const { current: documentCanvas } = canvasLeftContentRef;
            if (zoomLeft > 0) {
                const newZoomLeft = zoomLeft - 5;

                const newArrayLeftPageRedacts = Array.from(documentRedact).map(
                    (element) => element.getRedactData()
                );
                const filterDocumentCanvas = documentCanvas.filter(
                    (e) => typeof e !== 'undefined'
                );
                const newArrayLeftDocumentSize = Array.from(
                    filterDocumentCanvas
                ).map((element) => ({
                    width: element.width,
                    height: element.height,
                }));
                SetSavedLeftDocumentSize(newArrayLeftDocumentSize);
                SetSavedRedactForLeftDocument(newArrayLeftPageRedacts);
                setZoomLeft(newZoomLeft);
            }
        }, [zoomLeft]);

        const [SavedRightDocumentSize, SetSavedRightDocumentSize] =
            React.useState<{ width: number; height: number }[]>([]);
        const [RedactRightDocumentSaved, SetSavedRedactForRightDocument] =
            React.useState<Redacts[][]>();

        const [zoomRight, setZoomRight] = React.useState<number>(20);
        /**
         * handle zoom-in right tooltip
         */
        const onHandleZoomRightIn = React.useCallback(() => {
            const { current: documentRedact } = redactRightContentRef;
            const { current: documentCanvas } = canvasRightContentRef;

            if (zoomRight < 100) {
                const newZoomRight = zoomRight + 5;

                const newArrayRightPageRedacts = Array.from(documentRedact).map(
                    (element) => element.getRedactData()
                );
                const filterDocumentCanvas = documentCanvas.filter(
                    (e) => typeof e !== 'undefined'
                );
                const newArrayRightDocumentSize = Array.from(
                    filterDocumentCanvas
                ).map((element) => ({
                    width: element.width,
                    height: element.height,
                }));
                SetSavedRightDocumentSize(newArrayRightDocumentSize);
                SetSavedRedactForRightDocument(newArrayRightPageRedacts);
                setZoomRight(newZoomRight);
            }
        }, [zoomRight]);
        /**
         * handle zoom-out right tooltip
         */
        const onHandleZoomRightOut = React.useCallback(() => {
            const { current: documentRedact } = redactRightContentRef;
            const { current: documentCanvas } = canvasRightContentRef;

            if (zoomRight > 0) {
                const newZoomRight = zoomRight - 5;

                const newArrayRightPageRedacts = Array.from(documentRedact).map(
                    (element) => element.getRedactData()
                );
                const filterDocumentCanvas = documentCanvas.filter(
                    (e) => typeof e !== 'undefined'
                );
                const newArrayRightDocumentSize = Array.from(
                    filterDocumentCanvas
                ).map((element) => ({
                    width: element.width,
                    height: element.height,
                }));
                SetSavedRightDocumentSize(newArrayRightDocumentSize);
                SetSavedRedactForRightDocument(newArrayRightPageRedacts);
                setZoomRight(newZoomRight);
            }
        }, [zoomRight]);

        /**
         * get element offset from specific element
         * @param {HTMLElement} el - scale to zooom the page
         *  @return  top and left offset.
         */
        const getElementOffset = (el: any): { top: number; left: number } => {
            let top = 0;
            let left = 0;
            let element = el;

            // Loop through the DOM tree
            // and add it's parent's offset to get page offset
            do {
                top += element.offsetTop || 0;
                left += element.offsetLeft || 0;
                element = element.offsetParent;
            } while (element);

            return {
                top,
                left,
            };
        };
        React.useEffect(() => {
            const { current: documentLeftCanvas } = canvasLeftContentRef;
            const { current: documentRightCanvas } = canvasRightContentRef;

            const newArrayLeftPageRedacts = Array.from(
                RedactLeftDocumentSaved ?? []
            ).map((element, index) => {
                if (
                    documentLeftCanvas.some(
                        (o, id) => typeof o !== 'undefined' && id === index
                    ) &&
                    SavedLeftDocumentSize.some(
                        (o, id) => typeof o !== 'undefined' && id === index
                    )
                ) {
                    const { width } = documentLeftCanvas[index];
                    const { height } = documentLeftCanvas[index];
                    return GetPageRedactBasedOnZoom(
                        SavedLeftDocumentSize[index].width /
                            SavedLeftDocumentSize[index].height /
                            (width / height),
                        element
                    );
                }
                return element;
            });
            SetSavedRedactForLeftDocument(newArrayLeftPageRedacts);

            const newArrayRightPageRedacts = Array.from(
                RedactRightDocumentSaved ?? []
            ).map((element, index) => {
                if (
                    documentRightCanvas.some(
                        (o, id) => typeof o !== 'undefined' && id === index
                    ) &&
                    SavedRightDocumentSize.some(
                        (o, id) => typeof o !== 'undefined' && id === index
                    )
                ) {
                    const { width } = documentRightCanvas[index];
                    const { height } = documentRightCanvas[index];
                    return GetPageRedactBasedOnZoom(
                        SavedRightDocumentSize[index].width /
                            SavedRightDocumentSize[index].height /
                            (width / height),
                        element
                    );
                }
                return element;
            });
            SetSavedRedactForRightDocument(newArrayRightPageRedacts);
        }, [zoomRight, zoomLeft, GetPageRedactBasedOnZoom]);

        React.useLayoutEffect(() => {
            const { current: documentRightRedact } = redactRightContentRef;
            const { current: documentLeftRedact } = redactLeftContentRef;

            documentRightRedact.forEach((element, index) => {
                if (RedactRightDocumentSaved?.some((o, i) => i === index && o))
                    element.setRedactData(
                        RedactRightDocumentSaved[index] ?? []
                    );
            });

            documentLeftRedact.forEach((element, index) => {
                if (RedactLeftDocumentSaved?.some((o, i) => i === index && o))
                    element.setRedactData(RedactLeftDocumentSaved[index] ?? []);
            });
        }, [zoomRight, zoomLeft]);

        const [isLeftRendered, setPageLeftRenderer] = useState<boolean>(false);
        const [isRightRendered, setPageRightRenderer] =
            useState<boolean>(false);
        const [leftRendererArray, setLeftRendererArray] = useState<
            Array<boolean>
        >([]);
        const [rightRendererArray, setRightRendererArray] = useState<
            Array<boolean>
        >([]);
        const [leftTooltipHeight, setLeftTooltipHeight] =
            React.useState<string>('auto');
        const [rightTooltipHeight, setRightTooltipHeight] =
            React.useState<string>('auto');
        const [leftTooltipMeasure, setLeftTooltipMeasure] =
            React.useState<LayoutOffset>({ width: 0, height: 0 });
        const [rightTooltipMeasure, setRightTooltipMeasure] =
            React.useState<LayoutOffset>({ width: 0, height: 0 });
        const [leftTooltipHint, setLeftTooltipHint] =
            React.useState<string>('auto');
        const [rightTooltipHint, setRightTooltipHint] =
            React.useState<string>('auto');
        const [isLeftDrawn, setIsLeftDrawn] = React.useState<boolean>(false);
        const [isRightDrawn, setIsRightDrawn] = React.useState<boolean>(false);

        const [
            isVerticesScrollingRightDocument,
            setVerticesScrollingRightDocument,
        ] = React.useState<boolean>(false);
        const [
            isVerticesScrollingLeftDocument,
            setVerticesScrollingLeftDocument,
        ] = React.useState<boolean>(false);
        // const [
        //     isAwaitSuccesfullScrollLeftDocument,
        //     setAwaitSuccessfulScrollLeftdocument,
        // ] = React.useState<boolean>(false);
        // const [
        //     isAwaitSuccesfullScrollRightDocument,
        //     setAwaitSuccessfulScrollRightdocument,
        // ] = React.useState<boolean>(false);

        React.useLayoutEffect(() => {
            DocumentMetaProps?.setTooltipMeasurement(rightTooltipMeasure);
        }, [leftTooltipMeasure, rightTooltipMeasure]);
        const [leftDocumentUri, setLeftDocumentUri] = useState<null | string>(
            null
        );
        const [rightDocumentUri, setRightDocumentUri] = useState<null | string>(
            null
        );

        const [leftFinallySuccessful, setLeftFinallySuccessful] =
            React.useState<boolean>(false);
        const [rightFinallySuccessful, setRightFinallySuccessful] =
            React.useState<boolean>(false);

        React.useLayoutEffect(() => {
            if (
                numPagesLeft ===
                leftRendererArray.filter((e) => e === true).length
            ) {
                setLeftFinallySuccessful(true);
            } else {
                setLeftFinallySuccessful(false);
            }
        }, [numPagesLeft, leftRendererArray]);
        React.useLayoutEffect(() => {
            if (
                numPagesRight ===
                rightRendererArray.filter((e) => e === true).length
            ) {
                setRightFinallySuccessful(true);
            } else {
                setRightFinallySuccessful(false);
            }
        }, [numPagesRight, rightRendererArray]);

        const zoomLeftCarry = useSelector(
            (state: any) => state.carryState.zoom.zoomLeft
        );
        const zoomRightCarry = useSelector(
            (state: any) => state.carryState.zoom.zoomRight
        );
        const zoomLeftSingleCarry = useSelector(
            (state: any) => state.carryState.zoom.zoomSingleLeft
        );
        const zoomRightSingleCarry = useSelector(
            (state: any) => state.carryState.zoom.zoomSingleRight
        );
        const isSingleDocument = React.useMemo(
            () => isLeftActive === false || isRightActive === false,
            [isLeftActive, isRightActive]
        );
        const [isPrevSingleDocument, setPrevSingleDocument] =
            useState<boolean>(false);

        React.useLayoutEffect(() => {
            if (isSingleDocument && isPrevSingleDocument !== isSingleDocument) {
                if (step && step > 1) {
                    dispatch(CarryAppActions.setZoomLeftDocument(zoomLeft));
                    dispatch(CarryAppActions.setZoomRightDocument(zoomRight));
                }

                setZoomLeft(zoomLeftSingleCarry);
                setZoomRight(zoomRightSingleCarry);
            } else if (
                isSingleDocument === false &&
                isPrevSingleDocument !== isSingleDocument
            ) {
                if (step && step > 1) {
                    dispatch(
                        CarryAppActions.setZoomSingleLeftDocument(zoomLeft)
                    );
                    dispatch(
                        CarryAppActions.setZoomSingleRightDocument(zoomRight)
                    );
                }

                setZoomLeft(zoomLeftCarry);
                setZoomRight(zoomRightCarry);
            }
        }, [isSingleDocument]);

        useUpdateEffect(() => {
            if (isSingleDocument) {
                dispatch(CarryAppActions.setZoomSingleLeftDocument(zoomLeft));
                dispatch(CarryAppActions.setZoomSingleRightDocument(zoomRight));
            } else {
                dispatch(CarryAppActions.setZoomLeftDocument(zoomLeft));
                dispatch(CarryAppActions.setZoomRightDocument(zoomRight));
            }
        }, [zoomLeft, zoomRight]);

        React.useImperativeHandle(
            ref,
            () => ({
                setPreloadDocument: () => {
                    setDocumentRightSuccess(false);
                    setRightDocumentPrefetch(true);
                    setDocumentLeftSuccess(false);
                    setLeftDocumentPrefetch(true);
                    setDocumentLeftSourceError(false);
                    setDocumentRightSourceError(false);

                    setPrevSingleDocument(isSingleDocument);
                    setDocumentSourceFinish(false);
                },
                setLoseContext: async () =>
                    new Promise(async (resolve) => {
                        const { current: canvasRight } = redactRightContentRef;
                        const { current: canvasLeft } = redactLeftContentRef;
                        const { current: canvasInnerLeft } =
                            innerLeftDocumentRef;
                        const { current: canvasInnerRight } =
                            innerRightDocumentRef;
                        if (canvasInnerRight) {
                            const {
                                pdfDocument: pdfDoc,
                                isPageCached,
                                isPageVisible,
                            } = canvasInnerRight.linkService.current;

                            if (pdfDoc && isPageCached() && isPageVisible()) {
                                // if (leftFinallySuccessful) {
                                //     await pdfDoc.cleanup();
                                // }
                                // await pdfDoc.destroy();
                            }
                        }

                        for (
                            let index = 0;
                            index < canvasRight.length;
                            // eslint-disable-next-line no-plusplus
                            index++
                        ) {
                            const elCanvasRight = canvasRight[index];
                            elCanvasRight.resetRenderer();
                        }

                        if (canvasInnerLeft) {
                            const {
                                pdfDocument: pdfDoc,
                                isPageCached,
                                isPageVisible,
                            } = canvasInnerLeft.linkService.current;
                            if (pdfDoc && isPageCached() && isPageVisible()) {
                                // if (rightFinallySuccessful) {
                                //     await pdfDoc.cleanup();
                                // }
                                // await pdfDoc.destroy();
                            }
                        }

                        for (
                            let index = 0;
                            index < canvasLeft.length;
                            // eslint-disable-next-line no-plusplus
                            index++
                        ) {
                            const elCanvasLeft = canvasLeft[index];
                            elCanvasLeft.resetRenderer();
                        }
                        // window.frozeCompositor();
                        resolve(null);
                    }),
                setRestoreContext: () => {
                    // window.activeCompositor();
                    const { current: canvasRight } = redactRightContentRef;
                    const { current: canvasLeft } = redactLeftContentRef;
                    canvasRight.forEach((e) => {
                        e.reinitRenderer();
                    });
                    canvasLeft.forEach((e) => {
                        e.reinitRenderer();
                    });
                },
                /**
                 * non nullable to viewer lifecycle
                 */
                setNonNullableLeftViewer: () => {
                    // setZoomLeft(5);
                    // setZoomRight(5);
                    // setSavedZoom(true);

                    setDocumentLeftKey(0);

                    setPageLeftRenderer(false);

                    setDocumentLeftSuccess(false);

                    setDocumentLeftError(false);

                    setDocumentLeftSourceError(false);

                    setLeftDocumentPrefetch(false);

                    setLeftTooltipHeight('auto');

                    setLeftTooltipMeasure({ width: 0, height: 0 });

                    setLeftTooltipHint('10px');

                    setIsLeftDrawn(false);

                    setLeftRendererArray([]);

                    setDocumentSourceFinish(true);
                },
                /**
                 * non nullable to viewer lifecycle
                 */
                setNonNullableRightViewer: () => {
                    // setZoomLeft(5);
                    // setZoomRight(5);
                    // setSavedZoom(true);

                    setDocumentRightKey(0);

                    setPageRightRenderer(false);

                    setDocumentRightSuccess(false);

                    setDocumentRightError(false);

                    setDocumentRightSourceError(false);

                    setRightDocumentPrefetch(false);

                    setRightTooltipHeight('auto');

                    setRightTooltipMeasure({ width: 0, height: 0 });

                    setRightTooltipHint('10px');

                    setIsRightDrawn(false);

                    setRightRendererArray([]);
                    setDocumentSourceFinish(true);
                },
                setLeftDocumentNullable: (value) => {
                    setLeftDocumentUri(value);
                },
                setRightDocumentNullable: (value) => {
                    setRightDocumentUri(value);
                },
            }),
            [isSingleDocument]
        );

        React.useLayoutEffect(() => {
            // setZoomLeft(5);
            // setZoomRight(5);
            setLeftDocumentUri(_leftDocumentUri);
            setRightDocumentUri(_rightDocumentUri);
            setDocumentLeftKey(0);
            setDocumentRightKey(0);
            setPageLeftRenderer(false);
            setPageRightRenderer(false);
            setDocumentLeftSuccess(false);
            setDocumentRightSuccess(false);
            setDocumentLeftError(false);
            setDocumentRightError(false);
            setDocumentLeftSourceError(false);
            setDocumentRightSourceError(false);

            if (isLeftActive) {
                setLeftDocumentPrefetch(false);
            }
            if (isRightActive) {
                setRightDocumentPrefetch(false);
            }
            setLeftTooltipHeight('auto');
            setRightTooltipHeight('auto');
            setLeftTooltipMeasure({ width: 0, height: 0 });
            setRightTooltipMeasure({ width: 0, height: 0 });
            setLeftTooltipHint('10px');
            setRightTooltipHint('10px');
            setIsLeftDrawn(false);
            setIsRightDrawn(false);
            setLeftRendererArray([]);
            setRightRendererArray([]);
            setDocumentSourceFinish(true);
        }, [_leftDocumentUri, _rightDocumentUri]);

        useEffect(() => {
            const leftPageCached =
                innerLeftDocumentRef.current?.linkService.current.isPageCached();

            const leftPageVisible =
                innerLeftDocumentRef.current?.linkService.current.isPageVisible();
            const rightPageCached =
                innerRightDocumentRef.current?.linkService.current.isPageCached();

            const rightPageVisible =
                innerRightDocumentRef.current?.linkService.current.isPageVisible();

            if (leftPageCached || leftPageVisible) {
                // innerLeftDocumentRef.current?.linkService.current.pdfDocument?.cleanup();
                setDocumentLeftSuccess(false);

                setDocumentLeftError(false);
                setLeftDocumentPrefetch(true);
                setDocumentLeftSourceError(false);
                setLeftDocumentUri(null);

                const { current: innerLeftDocument } = innerLeftDocumentRef;
                if (innerLeftDocument) {
                    const { current: linkService } =
                        innerLeftDocument.linkService;
                    if (linkService && linkService.pdfDocument) {
                        const transportReader =
                            // eslint-disable-next-line no-underscore-dangle
                            linkService.pdfDocument._transport._networkStream
                                ._fullRequestReader;

                        const abortController: AbortController =
                            // eslint-disable-next-line no-underscore-dangle
                            transportReader._abortController;
                        if (abortController.signal.aborted === false) {
                            abortController.abort();
                        }
                    }
                }

                // innerLeftDocumentRef.current?.linkService.current.pdfDocument?.destroy();
            } else {
                setDocumentLeftSuccess(false);
                setDocumentLeftSourceError(false);
                setLeftDocumentUri(null);
                setLeftDocumentPrefetch(true);
            }
            if (rightPageCached || rightPageVisible) {
                // innerRightDocumentRef.current?.linkService.current.pdfDocument?.cleanup();
                setDocumentRightSuccess(false);
                setDocumentRightError(false);
                setRightDocumentPrefetch(true);
                setDocumentRightSourceError(false);
                setRightDocumentUri(null);
                const { current: innerRightDocument } = innerRightDocumentRef;
                if (innerRightDocument) {
                    const { current: linkService } =
                        innerRightDocument.linkService;
                    if (linkService && linkService.pdfDocument) {
                        const transportReader =
                            // eslint-disable-next-line no-underscore-dangle
                            linkService.pdfDocument._transport._networkStream
                                ._fullRequestReader;
                        const abortController: AbortController =
                            // eslint-disable-next-line no-underscore-dangle
                            transportReader._abortController;
                        if (abortController.signal.aborted === false) {
                            abortController.abort();
                        }
                    }
                }
                // innerRightDocumentRef.current?.linkService.current.pdfDocument?.destroy();
            } else {
                setDocumentRightSuccess(false);
                setRightDocumentUri(null);
                setRightDocumentPrefetch(true);
                setDocumentRightSourceError(false);
            }

            redactLeftContentRef.current = [];
            canvasLeftContentRef.current = [];
            redactRightContentRef.current = [];
            canvasRightContentRef.current = [];
            setLeftRendererArray([]);
            setRightRendererArray([]);
            setLeftTooltipHeight('auto');
            setRightTooltipHeight('auto');
            setLeftTooltipMeasure({ width: 0, height: 0 });
            setRightTooltipMeasure({ width: 0, height: 0 });
            setVerticesScrollingLeftDocument(false);
            setVerticesScrollingRightDocument(false);
            // setAwaitSuccessfulScrollLeftdocument(false);
            // setAwaitSuccessfulScrollRightdocument(false);
            setNumPagesLeft(null);
            setNumPagesRight(null);
            setLeftFinallySuccessful(false);
            setRightFinallySuccessful(false);
            if (step && step > 0) {
                removeScroll();
                setTimeOutScroll(false);
                setTimeOutScrollingFirst(false);
                setTimeOutScrollingSecond(false);
                Array.from({ length: sizeScroll }).forEach(() => {
                    removeScroll();
                });

                Array.from({ length: sizeVerticesScroll }).forEach(() => {
                    removeVerticesScroll();
                });
            }
        }, [step]);

        const [isMeasurementChanged, setChangeMeasurement] =
            useState<boolean>(false);

        const handleChangeMeasurement = React.useCallback(() => {
            setChangeMeasurement(true);
        }, []);

        useLayoutEffect(() => {
            const { current: leftTooltip } = innerLeftTooltip;
            const { current: rightTooltip } = innerRightTooltip;

            if (leftTooltip?.clientHeight && rightTooltip?.clientHeight) {
                if (leftTooltip.scrollHeight >= rightTooltip.scrollHeight) {
                    setRightTooltipHeight(`${leftTooltip.scrollHeight}px`);
                    setLeftTooltipHeight(`${leftTooltip.scrollHeight}px`);
                    setLeftTooltipMeasure({
                        width: leftTooltip.scrollWidth,
                        height: leftTooltip.scrollHeight,
                    });
                    setRightTooltipMeasure({
                        width: leftTooltip.scrollWidth,
                        height: leftTooltip.scrollHeight,
                    });
                    setChangeMeasurement(false);
                    // setLeftTooltipHint(`${leftTooltip.scrollHeight - 80 + 10}px`);
                    // setRightTooltipHint(`${leftTooltip.scrollHeight - 80 + 10}px`);
                } else if (
                    leftTooltip.scrollHeight < rightTooltip.scrollHeight
                ) {
                    setLeftTooltipHeight(`${rightTooltip.scrollHeight}px`);
                    setRightTooltipHeight(`${rightTooltip.scrollHeight}px`);
                    setLeftTooltipMeasure({
                        width: rightTooltip.scrollWidth,
                        height: rightTooltip.scrollHeight,
                    });
                    setRightTooltipMeasure({
                        width: rightTooltip.scrollWidth,
                        height: rightTooltip.scrollHeight,
                    });
                    setChangeMeasurement(false);
                    // setLeftTooltipHint(`${rightTooltip.scrollHeight - 80 + 10}px`);
                    // setRightTooltipHint(`${rightTooltip.scrollHeight - 80 + 10}px`);
                }
            } else if (leftTooltip?.clientHeight) {
                setRightTooltipHeight(`${leftTooltip.scrollHeight}px`);
                setLeftTooltipHeight(`${leftTooltip.scrollHeight}px`);
                setLeftTooltipMeasure({
                    width: leftTooltip.scrollWidth,
                    height: leftTooltip.scrollHeight,
                });
                setRightTooltipMeasure({
                    width: leftTooltip.scrollWidth,
                    height: leftTooltip.scrollHeight,
                });
                setChangeMeasurement(false);
            } else if (rightTooltip?.clientHeight) {
                setLeftTooltipHeight(`${rightTooltip.scrollHeight}px`);
                setRightTooltipHeight(`${rightTooltip.scrollHeight}px`);
                setLeftTooltipMeasure({
                    width: rightTooltip.scrollWidth,
                    height: rightTooltip.scrollHeight,
                });
                setRightTooltipMeasure({
                    width: rightTooltip.scrollWidth,
                    height: rightTooltip.scrollHeight,
                });
                setChangeMeasurement(false);
            }
        }, [isLeftRendered, isRightRendered, isMeasurementChanged]);

        const isReadyScrolling = useMemo(
            () =>
                isTimeoutScroll === true &&
                (isVerticesScrollingRightDocument ||
                    isVerticesScrollingLeftDocument),
            [
                isVerticesScrollingLeftDocument,
                isVerticesScrollingRightDocument,
                isTimeoutScroll,
            ]
        );
        const isReadySecondScrolling = useMemo(
            () =>
                isTimeoutScrollingFirst === false &&
                isReadyScrolling === true &&
                scrollingLeftDocument === false &&
                scrollingRightDocument === false,
            [
                isTimeoutScrollingFirst,
                isReadyScrolling,
                scrollingLeftDocument,
                scrollingRightDocument,
            ]
        );
        const harmonicTimeoutFN = React.useCallback(() => {
            setTimeOutScroll(false);

            removeScroll();
            removeScroll();
        }, [removeScroll]);
        const delayTimeoutScroll = React.useMemo(
            () =>
                typeof firstScroll === 'function' &&
                typeof lastScroll === 'function'
                    ? 600
                    : 400,
            [isTimeoutScroll]
        );
        useHarmonicIntervalFn(
            harmonicTimeoutFN,
            isTimeoutScroll && isReadyScrolling && isReadySecondScrolling
                ? delayTimeoutScroll
                : null
        );

        const harmonicTimeoutFirstFN = React.useCallback(() => {
            if (typeof firstScroll === 'function') {
                firstScroll();
            }

            setTimeOutScrollingFirst(false);
        }, [firstScroll]);
        const harmonicTimeoutSecondFN = React.useCallback(() => {
            if (typeof lastScroll === 'function') {
                lastScroll();
            }
            setTimeOutScrollingSecond(false);
        }, [lastScroll]);
        useHarmonicIntervalFn(
            harmonicTimeoutFirstFN,
            isTimeoutScrollingFirst && isReadyScrolling ? 200 : null
        );
        useHarmonicIntervalFn(
            harmonicTimeoutSecondFN,
            isTimeoutScrollingSecond &&
                isReadyScrolling &&
                isReadySecondScrolling
                ? 200
                : null
        );
        React.useLayoutEffect(() => {
            const leftVertices =
                leftVerticesData &&
                Array.isArray(leftVerticesData) &&
                leftVerticesData.length > 0 &&
                leftVerticesData.some((i) => i.pageNumber);
            const rightVertices =
                rightVerticesData &&
                Array.isArray(rightVerticesData) &&
                rightVerticesData.length > 0 &&
                rightVerticesData.some((i) => i.pageNumber);
            const count = [leftVertices, rightVertices].filter(
                (e) => e === true
            ).length;
            if (count === sizeScroll && sizeScroll !== 0) {
                if (typeof firstScroll === 'function') {
                    setTimeOutScrollingFirst(true);
                }
                if (typeof lastScroll === 'function') {
                    setTimeOutScrollingSecond(true);
                }
                setTimeOutScroll(true);
            }
        }, [sizeScroll]);

        const [isTimeoutScrollVertices, setTimeOutScrollVertices] =
            useState<boolean>(false);
        const [
            isTimeoutScrollingFirstVertices,
            setTimeOutScrollingFirstVertices,
        ] = useState<boolean>(false);
        const [
            isTimeoutScrollingSecondVertices,
            setTimeOutScrollingSecondVertices,
        ] = useState<boolean>(false);

        const harmonicTimeoutVerticesFN = React.useCallback(() => {
            setTimeOutScrollVertices(false);

            removeVerticesScroll();
            removeVerticesScroll();
        }, [removeVerticesScroll]);
        const delayTimeoutVerticesScroll = React.useMemo(
            () =>
                typeof firstVerticesScroll === 'function' &&
                typeof lastVerticesScroll === 'function'
                    ? 1200
                    : 400,
            [isTimeoutScrollVertices]
        );

        useHarmonicIntervalFn(
            harmonicTimeoutVerticesFN,
            isTimeoutScrollVertices ? delayTimeoutVerticesScroll : null
        );
        const harmonicTimeoutFirstVerticesFN = React.useCallback(() => {
            if (typeof firstVerticesScroll === 'function') {
                firstVerticesScroll();
            }
            setTimeOutScrollingFirstVertices(false);
        }, [firstVerticesScroll]);
        useHarmonicIntervalFn(
            harmonicTimeoutFirstVerticesFN,
            isTimeoutScrollingFirstVertices ? 200 : null
        );
        const harmonicTimeoutSecondVerticesFN = React.useCallback(() => {
            if (typeof lastVerticesScroll === 'function') {
                lastVerticesScroll();
            }
            setTimeOutScrollingSecondVertices(false);
        }, [lastVerticesScroll]);

        useHarmonicIntervalFn(
            harmonicTimeoutSecondVerticesFN,
            isTimeoutScrollingSecondVertices ? 1000 : null
        );
        React.useLayoutEffect(() => {
            const leftVertices =
                leftVerticesData &&
                Array.isArray(leftVerticesData) &&
                leftVerticesData.length > 0 &&
                leftVerticesData.some((i) => i.pageNumber);
            const rightVertices =
                rightVerticesData &&
                Array.isArray(rightVerticesData) &&
                rightVerticesData.length > 0 &&
                rightVerticesData.some((i) => i.pageNumber);
            const count = [leftVertices, rightVertices].filter(
                (e) => e === true
            ).length;

            if (count === sizeVerticesScroll && sizeVerticesScroll !== 0) {
                if (typeof firstVerticesScroll === 'function') {
                    setTimeOutScrollingFirstVertices(true);
                }
                if (typeof lastVerticesScroll === 'function') {
                    setTimeOutScrollingSecondVertices(true);
                }
                setTimeOutScrollVertices(true);
            }
        }, [sizeVerticesScroll]);
        const getPagesOffsetHeight = (
            arrayDivElement: inferRedact[]
        ): number => {
            const totalHeight = arrayDivElement.reduce(
                (previousValue: number, currentValue: inferRedact): number => {
                    const childrenOverlay: PageViewportProps =
                        currentValue.getViewport();

                    return previousValue + childrenOverlay.height;
                },
                0
            );
            return totalHeight;
        };
        /**
         * Jump to particular redacted page after render pdf finish
         * @summary to update document view using scroll behavior
         */
        useLayoutEffect(() => {
            if (
                isLeftRendered &&
                isSuccessLoadedLeft &&
                leftFinallySuccessful &&
                leftVerticesData &&
                Array.isArray(leftVerticesData) &&
                leftVerticesData.length > 0 &&
                leftVerticesData.some((i) => i.pageNumber)
            ) {
                const verticesSortByPage = leftVerticesData
                    ?.sort((a, b) => a.pageNumber - b.pageNumber)
                    .sort((a, b) => {
                        if (stack) {
                            const threeshold =
                                stack.percentageMatchThreeshold ?? 0;
                            if (threeshold > 0) {
                                if (
                                    parseInt(a.percentageMatch ?? '0', 10) >
                                        threeshold &&
                                    parseInt(b.percentageMatch ?? '0', 10) <
                                        threeshold
                                ) {
                                    return -1;
                                }
                                if (
                                    parseInt(b.percentageMatch ?? '0', 10) >
                                        threeshold &&
                                    parseInt(a.percentageMatch ?? '0', 10) <
                                        threeshold
                                ) {
                                    return 1;
                                }
                            }
                        }
                        return 0;
                    });
                const isPageFinder = verticesSortByPage.some(
                    (e) => e.pageFinder === true
                );
                const page = verticesSortByPage
                    .filter(
                        (t) =>
                            (isPageFinder && t.pageFinder === true) ||
                            isPageFinder === false
                    )
                    ?.at(0)?.pageNumber;
                const verticesX = verticesSortByPage
                    .filter(
                        (t) =>
                            (isPageFinder && t.pageFinder === true) ||
                            isPageFinder === false
                    )
                    ?.at(0)?.x1;
                const verticesY = verticesSortByPage
                    .filter(
                        (t) =>
                            (isPageFinder && t.pageFinder === true) ||
                            isPageFinder === false
                    )
                    ?.at(0)?.y1;
                if (page) {
                    const { current: innerLeftDocumentViewer } =
                        innerLeftDocumentRef;
                    if (innerLeftDocumentViewer) {
                        const { viewer } = innerLeftDocumentViewer;

                        if (viewer) {
                            const offsetHeight = getPagesOffsetHeight(
                                redactLeftContentRef.current.filter(
                                    (e, i) => i < page - 1
                                )
                            );
                            const pageScale = redactLeftContentRef.current.at(
                                page - 1
                            );
                            const currentPageScale = pageScale?.getPageScale();
                            const transY = verticesY ?? 0;
                            const translateY = currentPageScale
                                ? currentPageScale * transY
                                : transY;
                            const divDocument =
                                scrollLeftDocumentInputRef.current;

                            if (divDocument) {
                                const { current: staticViewer } = viewer;
                                addScroll(
                                    staticViewer.scrollTo.bind(this, {
                                        offset: offsetHeight,
                                        pageNumber: page,
                                        top:
                                            translateY -
                                            divDocument.clientHeight / 2,
                                        left: verticesX ?? 0,
                                        behavior: 'smooth',
                                    })
                                );
                            }

                            setVerticesScrollingLeftDocument(true);
                        }
                    }
                }
            }
        }, [isLeftRendered, isSuccessLoadedLeft, leftFinallySuccessful]);

        /**
         * Jump to particular redacted page after render pdf finish
         * @summaryto to update document view using scroll behavior
         */
        useLayoutEffect(() => {
            if (
                isRightRendered &&
                isSuccessLoadedRight &&
                rightFinallySuccessful &&
                rightVerticesData &&
                Array.isArray(rightVerticesData) &&
                rightVerticesData.length > 0 &&
                rightVerticesData.some((i) => i.pageNumber)
            ) {
                const verticesSortByPage = rightVerticesData
                    ?.sort((a, b) => a.pageNumber - b.pageNumber)
                    .sort((a, b) => {
                        if (stack) {
                            const threeshold =
                                stack.percentageMatchThreeshold ?? 0;
                            if (threeshold > 0) {
                                if (
                                    parseInt(a.percentageMatch ?? '0', 10) >=
                                        threeshold &&
                                    parseInt(b.percentageMatch ?? '0', 10) <
                                        threeshold
                                ) {
                                    return -1;
                                }
                                if (
                                    parseInt(b.percentageMatch ?? '0', 10) >=
                                        threeshold &&
                                    parseInt(a.percentageMatch ?? '0', 10) <
                                        threeshold
                                ) {
                                    return 1;
                                }
                            }
                        }
                        return 0;
                    });

                const isPageFinder = verticesSortByPage.some(
                    (e) => e.pageFinder === true
                );
                const page = verticesSortByPage
                    .filter(
                        (t) =>
                            (isPageFinder && t.pageFinder === true) ||
                            isPageFinder === false
                    )
                    ?.at(0)?.pageNumber;
                const verticesX = verticesSortByPage
                    .filter(
                        (t) =>
                            (isPageFinder && t.pageFinder === true) ||
                            isPageFinder === false
                    )
                    ?.at(0)?.x1;
                const verticesY = verticesSortByPage
                    .filter(
                        (t) =>
                            (isPageFinder && t.pageFinder === true) ||
                            isPageFinder === false
                    )
                    ?.at(0)?.y1;
                if (page) {
                    const { current: innerRightDocumentViewer } =
                        innerRightDocumentRef;
                    if (innerRightDocumentViewer) {
                        const { viewer } = innerRightDocumentViewer;

                        if (viewer) {
                            const offsetHeight = getPagesOffsetHeight(
                                redactRightContentRef.current.filter(
                                    (e, i) => i < page - 1
                                )
                            );
                            const pageScale = redactRightContentRef.current.at(
                                page - 1
                            );
                            const currentPageScale = pageScale?.getPageScale();
                            const transY = verticesY ?? 0;
                            const translateY = currentPageScale
                                ? currentPageScale * transY
                                : transY;
                            const { current: staticViewer } = viewer;
                            const divDocument =
                                scrollLeftDocumentInputRef.current;

                            if (divDocument) {
                                addScroll(
                                    staticViewer.scrollTo.bind(this, {
                                        offset: offsetHeight,
                                        pageNumber: page,
                                        top:
                                            translateY -
                                            divDocument.clientHeight / 2,
                                        left: verticesX ?? 0,
                                        behavior: 'smooth',
                                    })
                                );
                            }
                            setVerticesScrollingRightDocument(true);
                        }
                    }
                }
            }
        }, [isRightRendered, isSuccessLoadedRight, rightFinallySuccessful]);

        /**
         * Jump to particular vertices shaped after rendering page finish
         * @summary to update document view using scrollToView
         */
        // React.useLayoutEffect(() => {
        //     if (
        //         scrollingLeftDocument === true &&
        //         isVerticesScrollingLeftDocument
        //     ) {
        //         setAwaitSuccessfulScrollLeftdocument(true);
        //     }
        //     if (
        //         isVerticesScrollingLeftDocument &&
        //         isAwaitSuccesfullScrollLeftDocument === true &&
        //         scrollingLeftDocument === false
        //     ) {
        //         if (
        //             leftVerticesData &&
        //             Array.isArray(leftVerticesData) &&
        //             leftVerticesData.length > 0 &&
        //             leftVerticesData.some((i) => i.pageNumber)
        //         ) {
        //             const verticesSortByPage = leftVerticesData?.sort(
        //                 (a, b) => a.pageNumber - b.pageNumber
        //             );
        //             const isPageFinder = verticesSortByPage.some(
        //                 (e) => e.pageFinder === true
        //             );
        //             const VerticeInitialByPageNum = verticesSortByPage
        //                 .filter(
        //                     (t) =>
        //                         (isPageFinder && t.pageFinder === true) ||
        //                         isPageFinder === false
        //                 )
        //                 ?.at(0);
        //             const VerticeInitialPageNum =
        //                 VerticeInitialByPageNum?.pageNumber;
        //             const VerticeOriginalIndex = leftVerticesData
        //                 .filter((e) => e.pageNumber === VerticeInitialPageNum)
        //                 .findIndex(
        //                     (e) =>
        //                         e.x1 === VerticeInitialByPageNum?.x1 &&
        //                         e.x2 === VerticeInitialByPageNum?.x2 &&
        //                         e.x3 === VerticeInitialByPageNum?.x3 &&
        //                         e.x4 === VerticeInitialByPageNum?.x4 &&
        //                         e.y1 === VerticeInitialByPageNum?.y1 &&
        //                         e.y2 === VerticeInitialByPageNum?.y2 &&
        //                         e.y3 === VerticeInitialByPageNum?.y3 &&
        //                         e.y4 === VerticeInitialByPageNum?.y4
        //                 );
        //             if (VerticeOriginalIndex > -1 && VerticeInitialPageNum) {
        //                 const { current: redactLeftCanvas } =
        //                     redactLeftContentRef;
        //                 const redactLeftVerticesPage =
        //                     redactLeftCanvas[VerticeInitialPageNum - 1];
        //                 if (redactLeftVerticesPage) {
        //                     addVerticesScroll(
        //                         redactLeftVerticesPage.scrollToSpecificVertices.bind(
        //                             this,
        //                             VerticeOriginalIndex
        //                         )
        //                     );
        //                 }
        //             }
        //         }
        //         setVerticesScrollingLeftDocument(false);
        //         setAwaitSuccessfulScrollLeftdocument(false);
        //     }
        // }, [scrollingLeftDocument, isVerticesScrollingLeftDocument]);

        /**
         * Jump to particular vertices shaped after rendering page finish
         * @summary to update document view using scrollToView
         */
        // React.useLayoutEffect(() => {
        //     if (
        //         scrollingRightDocument === true &&
        //         isVerticesScrollingRightDocument
        //     ) {
        //         setAwaitSuccessfulScrollRightdocument(true);
        //     }
        //     if (
        //         isVerticesScrollingRightDocument &&
        //         isAwaitSuccesfullScrollRightDocument === true &&
        //         scrollingRightDocument === false
        //     ) {
        //         if (
        //             rightVerticesData &&
        //             Array.isArray(rightVerticesData) &&
        //             rightVerticesData.length > 0 &&
        //             rightVerticesData.some((i) => i.pageNumber)
        //         ) {
        //             const verticesSortByPage = rightVerticesData?.sort(
        //                 (a, b) => a.pageNumber - b.pageNumber
        //             );
        //             const isPageFinder = verticesSortByPage.some(
        //                 (e) => e.pageFinder === true
        //             );
        //             const VerticeInitialByPageNum = verticesSortByPage
        //                 .filter(
        //                     (t) =>
        //                         (isPageFinder && t.pageFinder === true) ||
        //                         isPageFinder === false
        //                 )
        //                 ?.at(0);

        //             const VerticeInitialPageNum =
        //                 VerticeInitialByPageNum?.pageNumber;
        //             const VerticeOriginalIndex = rightVerticesData
        //                 .filter((e) => e.pageNumber === VerticeInitialPageNum)
        //                 .findIndex(
        //                     (e) =>
        //                         e.x1 === VerticeInitialByPageNum?.x1 &&
        //                         e.x2 === VerticeInitialByPageNum?.x2 &&
        //                         e.x3 === VerticeInitialByPageNum?.x3 &&
        //                         e.x4 === VerticeInitialByPageNum?.x4 &&
        //                         e.y1 === VerticeInitialByPageNum?.y1 &&
        //                         e.y2 === VerticeInitialByPageNum?.y2 &&
        //                         e.y3 === VerticeInitialByPageNum?.y3 &&
        //                         e.y4 === VerticeInitialByPageNum?.y4
        //                 );

        //             if (VerticeOriginalIndex > -1 && VerticeInitialPageNum) {
        //                 const { current: redactRightCanvas } =
        //                     redactRightContentRef;
        //                 const redactRightVerticesPage =
        //                     redactRightCanvas[VerticeInitialPageNum - 1];
        //                 if (redactRightVerticesPage) {
        //                     addVerticesScroll(
        //                         redactRightVerticesPage.scrollToSpecificVertices.bind(
        //                             this,
        //                             VerticeOriginalIndex
        //                         )
        //                     );
        //                 }
        //             }
        //         }
        //         setVerticesScrollingRightDocument(false);
        //         setAwaitSuccessfulScrollRightdocument(false);
        //     }
        // }, [scrollingRightDocument, isVerticesScrollingRightDocument]);
        /**
         * Get left vertices data based on measured canvas.
         * @summary to update vertices with zoom value
         * @param {number} pageNumber - page number
         * @return {Vertices[]} changes vertices.
         */
        const GetLeftVerticesDataWithMeasuredCanvas = React.useCallback(
            (pageNumber: number) => {
                const { current: redactLeftCanvas } = redactLeftContentRef;
                if (leftVerticesData) {
                    const resizedVerticesData = leftVerticesData.reduce<
                        Vertices[][]
                    >((initialValues, currentValue, currentIndex) => {
                        if (
                            redactLeftCanvas[pageNumber - 1] &&
                            currentValue.pageNumber === pageNumber
                        ) {
                            const viewport =
                                redactLeftCanvas[pageNumber - 1].getViewport();
                            const values = initialValues;
                            if (
                                Array.isArray(values[pageNumber - 1]) === false
                            ) {
                                values[pageNumber - 1] = [];
                            }

                            values[pageNumber - 1].push({
                                isCorrected:
                                    isSingleDocument &&
                                    isSignCheck === false &&
                                    (currentValue.isDrawn === false ||
                                        typeof currentValue.isDrawn ===
                                            'undefined')
                                        ? footerCategory === 'Correct'
                                        : undefined,
                                isShown:
                                    currentValue.pageFinder === false ||
                                    typeof currentValue.pageFinder ===
                                        'undefined',
                                indices: currentIndex,
                                isActive: currentValue.isDrawn ?? false,
                                color: currentValue.color,
                                percentageMatch: currentValue.percentageMatch,
                                startPoint: new THREE.Vector3(
                                    currentValue.x1,
                                    currentValue.y1,
                                    1
                                ).multiplyScalar(viewport.scale),
                                endPoint: new THREE.Vector3(
                                    currentValue.x3,
                                    currentValue.y3,
                                    1
                                ).multiplyScalar(viewport.scale),
                            });
                        }
                        return {
                            ...initialValues,
                            isCorrected:
                                isSingleDocument &&
                                isSignCheck === false &&
                                (currentValue.isDrawn === false ||
                                    typeof currentValue.isDrawn === 'undefined')
                                    ? footerCategory === 'Correct'
                                    : undefined,
                            indices: currentIndex,
                            isActive: currentValue.isDrawn ?? false,
                        };
                    }, []);
                    return resizedVerticesData[pageNumber - 1];
                }
                return [];
            },
            [footerCategory, isSignCheck, isSingleDocument, leftVerticesData]
        );

        /**
         * Get right vertices data based on measured canvas.
         * @summary to update vertices with zoom value
         * @param {number} pageNumber - page number
         * @return {Vertices[]} changes vertices.
         */
        const GetRightVerticesDataWithMeasuredCanvas = React.useCallback(
            (pageNumber: number) => {
                if (rightVerticesData) {
                    const { current: redactRightCanvas } =
                        redactRightContentRef;

                    const resizedVerticesData = rightVerticesData.reduce<
                        Vertices[][]
                    >((initialValues, currentValue, currentIndex) => {
                        if (
                            redactRightCanvas[pageNumber - 1] &&
                            currentValue.pageNumber === pageNumber
                        ) {
                            const viewport =
                                redactRightCanvas[pageNumber - 1].getViewport();
                            const values = initialValues;
                            if (
                                Array.isArray(values[pageNumber - 1]) === false
                            ) {
                                values[pageNumber - 1] = [];
                            }
                            values[pageNumber - 1].push({
                                isCorrected:
                                    isSingleDocument &&
                                    isSignCheck === false &&
                                    (currentValue.isDrawn === false ||
                                        typeof currentValue.isDrawn ===
                                            'undefined')
                                        ? footerCategory === 'Correct'
                                        : undefined,
                                isShown:
                                    currentValue.pageFinder === false ||
                                    typeof currentValue.pageFinder ===
                                        'undefined',
                                indices: currentIndex,
                                isActive: currentValue.isDrawn ?? false,
                                color: currentValue.color,
                                percentageMatch: currentValue.percentageMatch,
                                startPoint: new THREE.Vector3(
                                    currentValue.x1,
                                    currentValue.y1,
                                    1
                                ).multiplyScalar(viewport.scale),
                                endPoint: new THREE.Vector3(
                                    currentValue.x3,
                                    currentValue.y3,
                                    1
                                ).multiplyScalar(viewport.scale),
                            });
                        }
                        return {
                            ...initialValues,
                            isCorrected:
                                isSingleDocument &&
                                isSignCheck === false &&
                                (currentValue.isDrawn === false ||
                                    typeof currentValue.isDrawn === 'undefined')
                                    ? footerCategory === 'Correct'
                                    : undefined,
                            indices: currentIndex,
                            isActive: currentValue.isDrawn ?? false,
                        };
                    }, []);
                    return resizedVerticesData[pageNumber - 1];
                }
                return [];
            },
            [footerCategory, isSignCheck, isSingleDocument, rightVerticesData]
        );

        /**
         * Handle left saved vertices remove.
         * @param {number} index - vertices indice
         */
        const handleLeftVerticesRemove = React.useCallback(
            (index: number) => {
                const { current: redactLeftCanvas } = redactLeftContentRef;

                let totalCount = (leftVerticesData?.length ?? 0) - 1;
                redactLeftCanvas.forEach((element) => {
                    totalCount += element.getTotalDrawnVertices();
                });
                if (totalCount === 0) setIsLeftDrawn(false);
                if (typeof emptyLeftVerticesData === 'function') {
                    emptyLeftVerticesData(index, true);
                }
            },
            [emptyLeftVerticesData, leftVerticesData?.length, zoomLeft]
        );
        /**
         * Handle left drawn vertices erase.
         * @param {number} index - vertices active indice
         *  @param {number} pageIndex - document page as index value
         */
        const handleLeftDrawnDeletation = React.useCallback(
            (index: number, pageIndex: number) => {
                const { current: redactLeftCanvas } = redactLeftContentRef;

                let totalCount = leftVerticesData?.length ?? 0;
                redactLeftCanvas.forEach((element, i) => {
                    if (i === pageIndex) {
                        element.setRedactDataByIndex(null, index);
                        totalCount += element.getTotalDrawnVertices() - 1;
                    } else {
                        totalCount += element.getTotalDrawnVertices();
                    }
                });
                if (totalCount === 0) {
                    setIsLeftDrawn(false);
                }
                if (typeof emptyLeftVerticesData === 'function') {
                    emptyLeftVerticesData(index, false);
                }
            },
            [emptyLeftVerticesData, leftVerticesData?.length]
        );
        /**
         * Handle left drawn vertices.
         * @param {THREE.Vector3} startpoint - start point of bounding box
         *  @param {THREE.Vector3} endpoint - end point of bounding box
         *  @param {number} index - document page as index value
         */
        const handleLeftDocumentOnDrawn = React.useCallback(
            (
                startpoint: THREE.Vector3,
                endpoint: THREE.Vector3,
                index: number
            ) => {
                const { current: documentLeftCanvas } = canvasLeftContentRef;
                const { current: redactLeftCanvas } = redactLeftContentRef;

                // const clientRect =
                //     documentLeftCanvas[index].getBoundingClientRect();
                const size = redactLeftCanvas[index].getViewport();
                const offset = getElementOffset(documentLeftCanvas[index]);

                const [startX, startY] = getClientCoords(
                    startpoint.x,
                    startpoint.y,
                    {
                        width: parseInt(
                            documentLeftCanvas[index].style.width.replace(
                                'px',
                                ''
                            ),
                            10
                        ),
                        height: parseInt(
                            documentLeftCanvas[index].style.height.replace(
                                'px',
                                ''
                            ),
                            10
                        ),
                        top: offset.top,
                        left: offset.left,
                    }
                );
                const [endX, endY] = getClientCoords(endpoint.x, endpoint.y, {
                    width: parseInt(
                        documentLeftCanvas[index].style.width.replace('px', ''),
                        10
                    ),
                    height: parseInt(
                        documentLeftCanvas[index].style.height.replace(
                            'px',
                            ''
                        ),
                        10
                    ),
                    top: offset.top,
                    left: offset.left,
                });
                const corners: number[][] = Array.from({ length: 4 }).map(
                    (o, i) => {
                        const corner: number[] = [];
                        switch (i) {
                            case 0:
                                corner.push(startX - offset.left);
                                corner.push(startY - offset.top);
                                break;
                            case 1:
                                corner.push(endX - offset.left);
                                corner.push(startY - offset.top);
                                break;
                            case 2:
                                corner.push(endX - offset.left);
                                corner.push(endY - offset.top);
                                break;
                            case 3:
                                corner.push(startX - offset.left);
                                corner.push(endY - offset.top);
                                break;
                            default:
                                break;
                        }
                        return corner;
                    }
                );
                if (typeof setLeftVerticesData === 'function') {
                    setLeftVerticesData(corners, index + 1, 1 / size.scale);
                }
                setIsLeftDrawn(true);
            },
            [setLeftVerticesData]
        );
        /**
         * Handle left saved vertices remove.
         * @param {number} index - vertices indice
         */
        const handleRightVerticesRemove = React.useCallback(
            (index: number) => {
                const { current: redactRightCanvas } = redactRightContentRef;

                let totalCount = (rightVerticesData?.length ?? 0) - 1;
                redactRightCanvas.forEach((element) => {
                    totalCount += element.getTotalDrawnVertices();
                });

                if (totalCount === 0) {
                    setIsRightDrawn(false);
                }
                if (typeof emptyRightVerticesData === 'function') {
                    emptyRightVerticesData(index, true);
                }
            },
            [emptyRightVerticesData, rightVerticesData?.length]
        );
        /**
         * Handle right drawn vertices erase.
         * @param {number} index - vertices active indice
         *  @param {number} pageIndex - document page as index value
         */
        const handleRightDrawnDeletation = React.useCallback(
            (index: number, pageIndex: number) => {
                const { current: redactRightCanvas } = redactRightContentRef;

                let totalCount = rightVerticesData?.length ?? 0;

                redactRightCanvas.forEach((element, ix) => {
                    if (ix === pageIndex) {
                        element.setRedactDataByIndex(null, index);
                        totalCount += element.getTotalDrawnVertices() - 1;
                    } else {
                        totalCount += element.getTotalDrawnVertices();
                    }
                });

                if (totalCount === 0) {
                    setIsRightDrawn(false);
                }
                if (typeof emptyRightVerticesData === 'function') {
                    emptyRightVerticesData(index, false);
                }
            },
            [emptyRightVerticesData, rightVerticesData?.length]
        );

        /**
         * Handle right drawn vertices.
         * @param {THREE.Vector3} startpoint - start point of bounding box
         *  @param {THREE.Vector3} endpoint - end point of bounding box
         *  @param {number} index - document page as index value
         */
        const handleRightDocumentOnDrawn = React.useCallback(
            (
                startpoint: THREE.Vector3,
                endpoint: THREE.Vector3,
                index: number
            ) => {
                const { current: documentRightCanvas } = canvasRightContentRef;
                const { current: redactRightCanvas } = redactRightContentRef;
                // const clientRect =
                //     documentRightCanvas[index].getBoundingClientRect();
                const size = redactRightCanvas[index].getViewport();

                const offset = getElementOffset(documentRightCanvas[index]);
                const [startX, startY] = getClientCoords(
                    startpoint.x,
                    startpoint.y,
                    {
                        width: parseInt(
                            documentRightCanvas[index].style.width.replace(
                                'px',
                                ''
                            ),
                            10
                        ),
                        height: parseInt(
                            documentRightCanvas[index].style.height.replace(
                                'px',
                                ''
                            ),
                            10
                        ),
                        top: getElementOffset(documentRightCanvas[index]).top,
                        left: getElementOffset(documentRightCanvas[index]).left,
                    }
                );

                const [endX, endY] = getClientCoords(endpoint.x, endpoint.y, {
                    width: parseInt(
                        documentRightCanvas[index].style.width.replace(
                            'px',
                            ''
                        ),
                        10
                    ),
                    height: parseInt(
                        documentRightCanvas[index].style.height.replace(
                            'px',
                            ''
                        ),
                        10
                    ),
                    top: getElementOffset(documentRightCanvas[index]).top,
                    left: getElementOffset(documentRightCanvas[index]).left,
                });
                const corners: number[][] = Array.from({ length: 4 }).map(
                    (o, i) => {
                        const corner: number[] = [];
                        switch (i) {
                            case 0:
                                corner.push(startX - offset.left);
                                corner.push(startY - offset.top);
                                break;
                            case 1:
                                corner.push(endX - offset.left);
                                corner.push(startY - offset.top);
                                break;
                            case 2:
                                corner.push(endX - offset.left);
                                corner.push(endY - offset.top);
                                break;
                            case 3:
                                corner.push(startX - offset.left);
                                corner.push(endY - offset.top);
                                break;
                            default:
                                break;
                        }
                        return corner;
                    }
                );
                if (typeof setRightVerticesData === 'function') {
                    setRightVerticesData(corners, index + 1, 1 / size.scale);
                }
                setIsRightDrawn(true);
            },
            [setRightVerticesData]
        );

        const handle = useFullScreenHandle();
        const [fullScreenModule, setFullScreenMode] = React.useState<string>();
        const actionFullScreenForLeftViewer = React.useCallback(() => {
            if (fullScreenModule === 'left') {
                handle.exit();
                setFullScreenMode('');
            } else {
                handle.enter();
                setFullScreenMode('left');
            }
        }, [fullScreenModule, handle]);
        const actionFullScreenForRightViewer = React.useCallback(() => {
            if (fullScreenModule === 'right') {
                handle.exit();
                setFullScreenMode('');
            } else {
                handle.enter();
                setFullScreenMode('right');
            }
        }, [fullScreenModule, handle]);

        const handleDocumentResetedByRefresh = React.useCallback(() => {
            // console.log('navigation');
            // navigation(-1);
        }, []);

        const [isDownloadLeftDocument, setDownloadLeftDocument] =
            useState<boolean>(false);

        /**
         * action for download button in the left tooltip.
         */
        const actionDownloadForLeftTooltip = React.useCallback(async () => {
            const { current: documentProxy } = innerLeftDocumentRef;

            if (documentProxy?.linkService) {
                setDownloadLeftDocument(true);
                await documentProxy?.linkService.current.pdfDocument
                    ?.getData()
                    .then(async (res) => {
                        const pdfDoc = await PDFDocument.load(res);

                        // const pages = pdfDoc.getPages();
                        // pages.forEach((element, index) => {
                        //     if (
                        //         leftVerticesData?.some(
                        //             (o) => o.pageNumber === index + 1
                        //         )
                        //     ) {
                        //         const verticesArray = leftVerticesData.filter(
                        //             (o) => o.pageNumber === index + 1
                        //         );
                        //         const PDFSpaces = element.getSize();
                        //         verticesArray.forEach((vertices) => {
                        //             if (vertices) {
                        //                 element.drawRectangle({
                        //                     x: vertices.x4,
                        //                     y: PDFSpaces.height - vertices.y4,
                        //                     width: vertices.x2 - vertices.x1,
                        //                     height: vertices.y4 - vertices.y1,
                        //                     borderWidth: 1,
                        //                     borderColor: rgb(1, 0, 0),
                        //                     opacity: 0,
                        //                     borderOpacity: 1,
                        //                 });
                        //             }
                        //         });
                        //     }
                        // });

                        const base64DataUri = await pdfDoc.saveAsBase64({
                            dataUri: true,
                        });

                        fetch(base64DataUri)
                            .then((resp) => resp.blob())
                            .then((blob) => {
                                setDownloadLeftDocument(false);
                                FileSaver.saveAs(blob, leftFilename);
                            })
                            .catch(() => {
                                setDownloadLeftDocument(false);
                            });
                    })
                    .catch(() => {
                        setDownloadLeftDocument(false);
                    });
            }
        }, [leftFilename]);

        const [isDownloadRightDocument, setDownloadRightDocument] =
            useState<boolean>(false);

        /**
         * action for download button in the right tooltip.
         */
        const actionDownloadForRightTooltip = React.useCallback(async () => {
            const { current: documentProxy } = innerRightDocumentRef;
            if (documentProxy?.linkService) {
                setDownloadRightDocument(true);
                await documentProxy?.linkService.current.pdfDocument
                    ?.getData()
                    .then(async (res) => {
                        const pdfDoc = await PDFDocument.load(res);

                        // const pages = pdfDoc.getPages();
                        // pages.forEach((element, index) => {
                        //     if (
                        //         rightVerticesData?.some(
                        //             (o) => o.pageNumber === index + 1
                        //         )
                        //     ) {
                        //         const verticesArray = rightVerticesData.filter(
                        //             (o) => o.pageNumber === index + 1
                        //         );
                        //         const PDFSpaces = element.getSize();
                        //         verticesArray.forEach((vertices) => {
                        //             if (vertices) {
                        //                 element.drawRectangle({
                        //                     x: vertices.x4,
                        //                     y: PDFSpaces.height - vertices.y4,
                        //                     width: vertices.x2 - vertices.x1,
                        //                     height: vertices.y4 - vertices.y1,
                        //                     borderWidth: 1,
                        //                     borderColor: rgb(1, 0, 0),
                        //                     opacity: 0,
                        //                     borderOpacity: 1,
                        //                 });
                        //             }
                        //         });
                        //     }
                        // });

                        const base64DataUri = await pdfDoc.saveAsBase64({
                            dataUri: true,
                        });

                        fetch(base64DataUri)
                            .then((resp) => resp.blob())
                            .then((blob) => {
                                setDownloadRightDocument(false);
                                FileSaver.saveAs(blob, rightFilename);
                            })
                            .catch(() => {
                                setDownloadRightDocument(false);
                            });
                    })
                    .catch(() => {
                        setDownloadLeftDocument(false);
                    });
            }
        }, [rightFilename]);
        /**
         * orientation used in printing service
         */
        const isPortraitOrientation = (size: any): boolean =>
            size.width <= size.height;
        const enablePrintAutoRotate = false;

        /**
         * get pages overview in printing service
         */
        const getPagesOverview = React.useCallback(
            (
                pages: Array<PDFPageProxy | undefined>
            ): { width: number; height: number; rotation: number }[] => {
                let initialOrientation: any;
                return pages.map((pageView) => {
                    if (pageView) {
                        const viewport = pageView.getViewport({ scale: 1 });
                        const orientation = isPortraitOrientation(viewport);
                        if (initialOrientation === undefined) {
                            initialOrientation = orientation;
                        } else if (
                            enablePrintAutoRotate &&
                            orientation !== initialOrientation
                        ) {
                            // Rotate to fit the initial orientation.
                            return {
                                width: viewport.height,
                                height: viewport.width,
                                rotation: (viewport.rotation - 90) % 360,
                            };
                        }
                        return {
                            width: viewport.width,
                            height: viewport.height,
                            rotation: viewport.rotation,
                        };
                    }
                    return {
                        width: 0,
                        height: 0,
                        rotation: 0,
                    };
                });
            },
            [enablePrintAutoRotate]
        );
        const [printService, setPrintService] =
            useState<PDFPrintService | null>(null);
        const [sidePrinting, setSidePrinting] = useState<'right' | 'left'>(
            'left'
        );
        const [isPrinting, setStartPrinting] = useState<boolean>(false);
        const [overlayManager, setOverlayManager] = useState<OverlayManager>();
        const [modifiedLeftDocumentProxy, setModifiedLeftDocumentProxy] =
            useState<PDFDocumentProxy | null>(null);
        const [modifiedRightDocumentProxy, setModifiedRightDocumentProxy] =
            useState<PDFDocumentProxy | null>(null);
        const [pagesOverviewPrintService, setPagesOverviewService] = useState<
            {
                width: number;
                height: number;
                rotation: number;
            }[]
        >([]);
        const innerPrintContainer = useRef<HTMLDivElement>();
        React.useLayoutEffect(() => {
            if (!overlayManager) {
                const overlayManagerInit = new OverlayManager();
                saveOverlayManager(overlayManagerInit);
                setOverlayManager(overlayManagerInit);
            }
        }, []);
        /**
         * left tooltip printing button
         */
        const actionTriggerHandleLeftDocument = React.useCallback(async () => {
            if (!overlayManager) {
                const overlayManagerPrintLeft = new OverlayManager();
                saveOverlayManager(overlayManagerPrintLeft);
                setOverlayManager(overlayManagerPrintLeft);
            }
            const { current: documentProxy } = innerLeftDocumentRef;
            if (documentProxy?.linkService) {
                const documentModifiedUrl =
                    await documentProxy?.linkService.current.pdfDocument
                        ?.getData()
                        .then(async (res) => {
                            const pdfDoc = await PDFDocument.load(res);

                            const pages = pdfDoc.getPages();
                            pages.forEach((element, index) => {
                                if (
                                    leftVerticesData?.some(
                                        (o) => o.pageNumber === index + 1
                                    )
                                ) {
                                    const verticesArray =
                                        leftVerticesData.filter(
                                            (o) => o.pageNumber === index + 1
                                        );
                                    const PDFSpaces = element.getSize();
                                    verticesArray.forEach((vertices) => {
                                        if (vertices) {
                                            element.drawRectangle({
                                                x: vertices.x4,
                                                y:
                                                    PDFSpaces.height -
                                                    vertices.y4,
                                                width:
                                                    vertices.x2 - vertices.x1,
                                                height:
                                                    vertices.y4 - vertices.y1,
                                                borderWidth: 1,
                                                borderColor: rgb(1, 0, 0),
                                                opacity: 0,
                                                borderOpacity: 1,
                                            });
                                        }
                                    });
                                }
                            });

                            const pdfBytes = await pdfDoc.save();
                            const bytes = new Uint8Array(pdfBytes);
                            const blob = new Blob([bytes], {
                                type: 'application/pdf',
                            });
                            const docUrl = URL.createObjectURL(blob);
                            return docUrl;
                        });
                if (documentModifiedUrl) {
                    getDocument(documentModifiedUrl).promise.then(
                        async (modifiedDocumentProxy) => {
                            const pages: Array<PDFPageProxy | undefined> =
                                await Promise.all(
                                    Array.from({
                                        length: modifiedDocumentProxy.numPages,
                                    }).map(
                                        async (
                                            o,
                                            index
                                        ): Promise<
                                            PDFPageProxy | undefined
                                        > => {
                                            const page: any =
                                                await modifiedDocumentProxy.getPage(
                                                    index + 1
                                                );
                                            return page;
                                        }
                                    )
                                );
                            const pagesOverview = getPagesOverview(pages);
                            handle.exit();
                            setModifiedLeftDocumentProxy(modifiedDocumentProxy);
                            setFullScreenMode('');
                            setPagesOverviewService(pagesOverview);
                            setSidePrinting('left');
                            window.print();
                        }
                    );
                }
            }
        }, [getPagesOverview, handle, leftVerticesData, overlayManager]);

        /**
         * right tooltip printing button
         */
        const actionTriggerHandleRightDocument = React.useCallback(async () => {
            if (!overlayManager) {
                const overlayManagerPrintRight = new OverlayManager();
                saveOverlayManager(overlayManagerPrintRight);
                setOverlayManager(overlayManagerPrintRight);
            }
            const { current: documentProxy } = innerRightDocumentRef;
            if (documentProxy?.linkService) {
                const documentModifiedUrl =
                    await documentProxy?.linkService.current.pdfDocument
                        ?.getData()
                        .then(async (res) => {
                            const pdfDoc = await PDFDocument.load(res);

                            const pages = pdfDoc.getPages();
                            pages.forEach((element, index) => {
                                if (
                                    rightVerticesData?.some(
                                        (o) => o.pageNumber === index + 1
                                    )
                                ) {
                                    const verticesArray =
                                        rightVerticesData.filter(
                                            (o) => o.pageNumber === index + 1
                                        );
                                    const PDFSpaces = element.getSize();
                                    verticesArray.forEach((vertices) => {
                                        if (vertices) {
                                            element.drawRectangle({
                                                x: vertices.x4,
                                                y:
                                                    PDFSpaces.height -
                                                    vertices.y4,
                                                width:
                                                    vertices.x2 - vertices.x1,
                                                height:
                                                    vertices.y4 - vertices.y1,
                                                borderWidth: 1,
                                                borderColor: rgb(1, 0, 0),
                                                opacity: 0,
                                                borderOpacity: 1,
                                            });
                                        }
                                    });
                                }
                            });

                            const pdfBytes = await pdfDoc.save();
                            const bytes = new Uint8Array(pdfBytes);
                            const blob = new Blob([bytes], {
                                type: 'application/pdf',
                            });
                            const docUrl = URL.createObjectURL(blob);
                            return docUrl;
                        });

                if (documentModifiedUrl) {
                    getDocument(documentModifiedUrl).promise.then(
                        async (modifiedDocumentProxy) => {
                            const pages: Array<PDFPageProxy | undefined> =
                                await Promise.all(
                                    Array.from({
                                        length: modifiedDocumentProxy.numPages,
                                    }).map(
                                        async (
                                            o,
                                            index
                                        ): Promise<
                                            PDFPageProxy | undefined
                                        > => {
                                            const page: any =
                                                await modifiedDocumentProxy.getPage(
                                                    index + 1
                                                );
                                            return page;
                                        }
                                    )
                                );
                            const pagesOverview = getPagesOverview(pages);
                            handle.exit();
                            setModifiedRightDocumentProxy(
                                modifiedDocumentProxy
                            );
                            setFullScreenMode('');
                            setPagesOverviewService(pagesOverview);
                            setSidePrinting('right');
                            window.print();
                        }
                    );
                }
            }
        }, [getPagesOverview, handle, overlayManager, rightVerticesData]);

        useLayoutEffect(() => {
            if (typeof handleHiddenFooter === 'function' && isPrinting)
                handleHiddenFooter();
            if (typeof handleShowFooter === 'function' && !isPrinting)
                handleShowFooter();
        }, [handleHiddenFooter, handleShowFooter, isPrinting]);

        /**
         * handle for beforeprint listener
         */
        const handleBeforePrint = React.useCallback(() => {
            const { current: documentRightProxy } = innerRightDocumentRef;
            const { current: documentLeftProxy } = innerLeftDocumentRef;
            if (
                documentRightProxy?.linkService &&
                sidePrinting === 'right' &&
                modifiedRightDocumentProxy
            ) {
                const printAnnotationStoragePromise =
                    modifiedRightDocumentProxy?.annotationStorage.print;

                if (printService) {
                    // There is no way to suppress beforePrint/afterPrint events,
                    // but PDFPrintService may generate double events -- this will ignore
                    // the second event that will be coming from native window.print().
                    return;
                }

                const { current: printContainer } = innerPrintContainer;
                const printResolution = 150;
                // const { optionalContentConfigPromise } = this.pdfViewer;
                const printServiceInstance = createPrintService(
                    modifiedRightDocumentProxy,
                    pagesOverviewPrintService,
                    printContainer,
                    printResolution,
                    null,
                    printAnnotationStoragePromise,
                    overlayManager
                );
                setStartPrinting(true);
                setPrintService(printServiceInstance);

                // need testing
                // before implementing the behavior
                // this.forceRendering();
                // // Disable the editor-indicator during printing (fixes bug 1790552).
                // this.setTitle();

                printServiceInstance.layout();

                // no annotation
                // if (this._hasAnnotationEditors) {
                //     this.externalServices.reportTelemetry({
                //         type: 'editing',
                //         data: { type: 'print' },
                //     });
                // }
            } else if (
                documentLeftProxy?.linkService &&
                sidePrinting === 'left' &&
                modifiedLeftDocumentProxy
            ) {
                const printAnnotationStoragePromise =
                    modifiedLeftDocumentProxy?.annotationStorage.print;

                if (printService) {
                    return;
                }

                const { current: printContainer } = innerPrintContainer;
                const printResolution = 150;
                // const { optionalContentConfigPromise } = this.pdfViewer;
                const printServiceInstance = createPrintService(
                    modifiedLeftDocumentProxy,
                    pagesOverviewPrintService,
                    printContainer,
                    printResolution,
                    null,
                    printAnnotationStoragePromise,
                    overlayManager
                );
                setStartPrinting(true);
                setPrintService(printServiceInstance);

                printServiceInstance.layout();
            }
        }, [
            modifiedLeftDocumentProxy,
            modifiedRightDocumentProxy,
            overlayManager,
            pagesOverviewPrintService,
            printService,
            sidePrinting,
        ]);
        /**
         * handle for afterprint listener
         */
        const handleAfterPrint = React.useCallback(() => {
            const { current: documentProxy } = innerRightDocumentRef;
            setStartPrinting(false);
            // if (this._printAnnotationStoragePromise) {
            //     this._printAnnotationStoragePromise.then(() => {
            //         this.pdfScriptingManager.dispatchDidPrint();
            //     });
            //     this._printAnnotationStoragePromise = null;
            // }
            if (documentProxy?.linkService) {
                if (printService) {
                    printService.destroy();
                    setPrintService(null);
                    documentProxy.linkService.current.pdfDocument?.annotationStorage.resetModified();
                }
            }

            // this.forceRendering();
            // // Re-enable the editor-indicator after printing (fixes bug 1790552).
            // this.setTitle();
        }, [printService]);

        /**
         * generate string for right document drawn status
         */
        const GenerateRightTitleForDrawnTooltip = React.useMemo((): string => {
            if (
                (Array.isArray(rightVerticesData) &&
                    rightVerticesData.length === 0) ||
                rightVerticesData === null
            ) {
                if (isRightDrawn) {
                    return 'Please refer to the colored boxes for comparison';
                }
                if (rightCompareValue === 'Not Found') {
                    return `Please create purple boxes for the correction`;
                }
                return `Please create purple boxes for the correction`;
            }
            return 'Please refer to the colored boxes for comparison';
        }, [isRightDrawn, rightCompareValue, rightVerticesData]);
        /**
         * generate string for left document drawn status
         */
        const GenerateLeftTitleForDrawnTooltip = React.useMemo((): string => {
            if (
                (Array.isArray(leftVerticesData) &&
                    leftVerticesData.length === 0) ||
                leftVerticesData === null
            ) {
                if (isLeftDrawn) {
                    return 'Please refer to the colored boxes for comparison';
                }
                if (leftCompareValue === 'Not Found') {
                    return `Please create purple boxes for the correction`;
                }
                return `Please create purple boxes for the correction`;
            }
            return 'Please refer to the colored boxes for comparison';
        }, [isLeftDrawn, leftCompareValue, leftVerticesData]);

        // React.useLayoutEffect(() => {
        //     if (footerCategory === 'Document Missing') {
        //         if (isLeftOriginalMissing) {
        //             const { current: redactLeftCanvas } = redactLeftContentRef;

        //             redactLeftCanvas.forEach((element) => {
        //                 element.removeDrawnVertices();
        //             });
        //         }
        //         if (isRightOriginalMissing) {
        //             const { current: redactRightCanvas } =
        //                 redactRightContentRef;

        //             redactRightCanvas.forEach((element) => {
        //                 element.removeDrawnVertices();
        //             });
        //         }
        //     }
        // }, [footerCategory]);
        /**
         * left available drawn condition
         */
        const isLeftAvailableDrawn = React.useMemo(() => {
            let isavail = false;
            if (
                footerCategory === 'Document Missing' ||
                isCanDrawnFromType === false
            ) {
                isavail = true;
            } else if (leftDrawn) {
                isavail = true;
            } else if (
                footerCategory === 'Correct' ||
                footerCategory === 'Hand Signed' ||
                footerCategory === 'Sign Not Verified' ||
                footerCategory === 'Escalated' ||
                footerCategory === 'N/A' ||
                footerCategory === 'Send For Rework'
            ) {
                isavail = true;
            } else if (
                (Array.isArray(leftVerticesData) &&
                    leftVerticesData.length === 0) ||
                leftVerticesData === null
            ) {
                isavail = true;
            } else {
                isavail = false;
            }

            return isavail;
        }, [footerCategory, isCanDrawnFromType, leftDrawn, leftVerticesData]);

        /**
         * right available drawn condition
         */
        const isRightAvailableDrawn = React.useMemo(() => {
            let isavail = false;
            if (
                footerCategory === 'Document Missing' ||
                isCanDrawnFromType === false
            ) {
                isavail = true;
            } else if (rightDrawn) {
                isavail = true;
            } else if (
                footerCategory === 'Correct' ||
                footerCategory === 'Hand Signed' ||
                footerCategory === 'Sign Not Verified' ||
                footerCategory === 'Escalated' ||
                footerCategory === 'N/A' ||
                footerCategory === 'Send For Rework'
            ) {
                isavail = true;
            } else if (
                (Array.isArray(rightVerticesData) &&
                    rightVerticesData.length === 0) ||
                rightVerticesData === null
            ) {
                isavail = true;
            } else {
                isavail = false;
            }

            return isavail;
        }, [footerCategory, isCanDrawnFromType, rightDrawn, rightVerticesData]);

        const { height } = useWindowSize();

        const handleRenderOffsetForContentView = React.useMemo(() => {
            const tooltipHeight =
                leftTooltipMeasure.height > rightTooltipMeasure.height
                    ? leftTooltipMeasure.height
                    : rightTooltipMeasure.height;
            const footerHeight = DocumentMetaProps
                ? DocumentMetaProps?.footerMeasurement.height
                : 0;
            const resizedHeight = height - tooltipHeight - footerHeight;

            if (resizedHeight > 0 && matches) {
                return {
                    height: `${resizedHeight}px !important`,
                };
            }
            return {
                height: 'auto',
            };
        }, [
            DocumentMetaProps,
            height,
            leftTooltipMeasure.height,
            matches,
            rightTooltipMeasure.height,
        ]);
        const handleRenderOffsetForSideView = React.useMemo(() => {
            const tooltipHeight =
                leftTooltipMeasure.height > rightTooltipMeasure.height
                    ? leftTooltipMeasure.height
                    : rightTooltipMeasure.height;
            const footerHeight = DocumentMetaProps
                ? DocumentMetaProps?.footerMeasurement.height
                : 0;
            const resizedHeight = height - tooltipHeight - footerHeight;
            if (resizedHeight > 0 && matches) {
                return {
                    height: `${resizedHeight}px !important`,
                };
            }
            return {
                height: 'auto',
            };
        }, [
            DocumentMetaProps,
            height,
            leftTooltipMeasure.height,
            matches,
            rightTooltipMeasure.height,
        ]);

        useEvent('beforeprint', handleBeforePrint);
        useEvent('afterprint', handleAfterPrint);

        return (
            <FullScreen
                handle={handle}
                className={fullScreenModule}
                onChange={(state) => {
                    if (!state) setFullScreenMode('');
                }}
            >
                <dialog id="printServiceDialog" style={{ minWidth: '200px' }}>
                    <div className="row">
                        <span data-l10n-id="print_progress_message">
                            Preparing document for printing…
                        </span>
                    </div>
                    <div className="row">
                        <progress value="0" max="100" />
                        <span
                            data-l10n-id="print_progress_percent"
                            data-l10n-args='{ "progress": 0 }'
                            className="relative-progress"
                        >
                            0%
                        </span>
                    </div>
                    <div className="buttonRow">
                        <Button id="printCancel" className="dialogButton">
                            <span data-l10n-id="print_progress_close">
                                Cancel
                            </span>
                        </Button>
                    </div>
                </dialog>
                <Box id="printContainer" ref={innerPrintContainer} />
                <Box
                    className={
                        isPrinting
                            ? `document-viewer-wrapper hidden printing`
                            : `document-viewer-wrapper`
                    }
                >
                    <Stack
                        className={
                            matches
                                ? 'document-stack row-stack'
                                : 'document-stack column-stack'
                        }
                        direction={matches ? 'row' : 'column'}
                        spacing={0}
                    >
                        {isLeftActive && (
                            <Grid
                                container
                                spacing={0}
                                className="document-grid left"
                            >
                                <Grid
                                    ref={innerLeftTooltip}
                                    container
                                    item
                                    xs={12}
                                    spacing={0}
                                    className="document-tooltip"
                                    sx={{
                                        height:
                                            leftTooltipHeight !== 'auto'
                                                ? `${leftTooltipHeight} !important`
                                                : '80px',
                                    }}
                                >
                                    <Grid
                                        component={Paper}
                                        container
                                        item
                                        xs={12}
                                        spacing={0}
                                        className={
                                            leftTooltipHeight === 'auto'
                                                ? 'auto-size'
                                                : undefined
                                        }
                                        elevation={24}
                                        sx={{
                                            height:
                                                leftTooltipHeight !== 'auto'
                                                    ? leftTooltipHeight
                                                    : undefined,
                                        }}
                                    >
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="h5">
                                                Category :{' '}
                                                <strong>{leftCategory}</strong>
                                                {isLeftOriginalMissing && (
                                                    <strong>
                                                        {' '}
                                                        (Document Missing)
                                                    </strong>
                                                )}
                                            </Typography>

                                            <Typography variant="h6">
                                                {leftFilename}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Stack direction="row" spacing={2}>
                                                <Box className="zoom-container">
                                                    <FormControl
                                                        fullWidth
                                                        variant="standard"
                                                        className="aggregator-zoom"
                                                    >
                                                        <InputLabel htmlFor="standard-adornment-amount">
                                                            Zoom
                                                        </InputLabel>
                                                        <Input
                                                            inputProps={{
                                                                'data-testid':
                                                                    'test-zoom-label',
                                                                role: 'spinbutton',
                                                                'aria-label':
                                                                    'zoom',
                                                                'aria-invalid':
                                                                    zoomLeft <
                                                                        0 ||
                                                                    zoomLeft >
                                                                        100
                                                                        ? 'true'
                                                                        : 'false',
                                                                'aria-valuenow':
                                                                    zoomLeft,
                                                                'aria-valuemax': 100,
                                                                'aria-valuemin': 0,
                                                            }}
                                                            disableUnderline
                                                            value={`${zoomLeft}%`}
                                                            disabled
                                                            id="standard-adornment-amount"
                                                            startAdornment={
                                                                <InputAdornment position="start">
                                                                    <IconButton
                                                                        disabled={
                                                                            !isSuccessLoadedLeft
                                                                        }
                                                                        title="zoom out"
                                                                        onClick={
                                                                            onHandleZoomLeftOut
                                                                        }
                                                                        //   onMouseDown={handleMouseDownPassword}
                                                                        edge="end"
                                                                    >
                                                                        <ZoomOut />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            }
                                                            endAdornment={
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        disabled={
                                                                            !isSuccessLoadedLeft
                                                                        }
                                                                        title="zoom in"
                                                                        onClick={
                                                                            onHandleZoomLeftIn
                                                                        }
                                                                        //   onMouseDown={handleMouseDownPassword}
                                                                        edge="end"
                                                                    >
                                                                        <ZoomIn />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            }
                                                        />
                                                    </FormControl>
                                                </Box>
                                                <IconButton
                                                    className="tooltip-button"
                                                    aria-label="fullscreen"
                                                    disabled={
                                                        !isSuccessLoadedLeft
                                                    }
                                                    onClick={
                                                        actionFullScreenForLeftViewer
                                                    }
                                                >
                                                    {fullScreenModule ===
                                                    'left' ? (
                                                        <CloseFullscreenIcon />
                                                    ) : (
                                                        <OpenInFullIcon />
                                                    )}
                                                </IconButton>
                                                <IconButton
                                                    className="tooltip-button hidden"
                                                    aria-label="print"
                                                    disabled={
                                                        !isSuccessLoadedLeft
                                                    }
                                                    onClick={
                                                        actionTriggerHandleLeftDocument
                                                    }
                                                >
                                                    <LocalPrintshopOutlinedIcon />
                                                </IconButton>
                                                <IconButton
                                                    className="tooltip-button"
                                                    aria-label="download"
                                                    disabled={
                                                        !isSuccessLoadedLeft
                                                    }
                                                    onClick={
                                                        actionDownloadForLeftTooltip
                                                    }
                                                >
                                                    {isDownloadLeftDocument ? (
                                                        <DownloadingOutlinedIcon />
                                                    ) : (
                                                        <FileDownloadOutlinedIcon />
                                                    )}
                                                </IconButton>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    spacing={0}
                                    className="document-viewer"
                                >
                                    <Grid
                                        item
                                        xs={leftViewOpen ? 12 : 0}
                                        md={leftViewOpen ? 3 : 0}
                                        className="sideview"
                                        sx={handleRenderOffsetForSideView}
                                    >
                                        <IconButton
                                            onClick={onHandleLeftSidewayToggle}
                                            className={
                                                leftViewOpen
                                                    ? 'tooltip-button'
                                                    : 'tooltip-button close-button'
                                            }
                                            aria-label="show sideview"
                                        >
                                            {matches && leftViewOpen && (
                                                <KeyboardDoubleArrowLeftIcon />
                                            )}
                                            {matches && !leftViewOpen && (
                                                <KeyboardDoubleArrowRightIcon />
                                            )}
                                            {!matches && leftViewOpen && (
                                                <KeyboardDoubleArrowUpIcon />
                                            )}
                                            {!matches && !leftViewOpen && (
                                                <KeyboardDoubleArrowDownIcon />
                                            )}
                                        </IconButton>

                                        {leftViewOpen && (
                                            <Document
                                                ref={outerLeftDocumentRef}
                                                className="viewer"
                                                file={leftDocumentUri}
                                                onLoadSuccess={
                                                    onDocumentLeftLoadSuccess
                                                }
                                                error={
                                                    <Box className="align-loader">
                                                        {delayedLeft ? (
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        ) : (
                                                            <Typography variant="h3">
                                                                {
                                                                    documentLeftError
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                noData={
                                                    <Box className="align-loader">
                                                        {isSuccessLoadedLeft ===
                                                            false ||
                                                        delayedLeft ? (
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        ) : (
                                                            <Typography variant="h3">
                                                                {
                                                                    documentLeftError
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                loading={
                                                    <Box className="align-loader">
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    </Box>
                                                }
                                            >
                                                {Array.from({
                                                    length: numPagesLeft ?? 0,
                                                }).map((el, index) => (
                                                    <Page
                                                        key={`sideviewleft-${index}`}
                                                        onClick={
                                                            onHandleLeftSideview
                                                        }
                                                        pageNumber={index + 1}
                                                        width={100}
                                                        height={100}
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={
                                                            false
                                                        }
                                                        loading={
                                                            <Box className="align-loader">
                                                                <CircularProgress
                                                                    className="mr-2 margin-loader"
                                                                    size={54}
                                                                    color="inherit"
                                                                    title="progress loading"
                                                                />
                                                            </Box>
                                                        }
                                                        error={
                                                            <Box className="align-loader">
                                                                <Typography variant="h3">
                                                                    Document
                                                                    Page Missing
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                ))}
                                            </Document>
                                        )}
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={leftViewOpen ? 9 : 12}
                                        className="contentview"
                                        sx={handleRenderOffsetForContentView}
                                    >
                                        {isSuccessLoadedLeft && (
                                            <Tooltip
                                                title={
                                                    GenerateLeftTitleForDrawnTooltip
                                                }
                                                placement="left-start"
                                            >
                                                <CropFreeIcon
                                                    sx={{
                                                        top: `${leftTooltipHint} !important`,
                                                    }}
                                                    className={
                                                        ((Array.isArray(
                                                            leftVerticesData
                                                        ) &&
                                                            leftVerticesData.length ===
                                                                0) ||
                                                            leftVerticesData ===
                                                                null) &&
                                                        isLeftDrawn === false &&
                                                        isLeftCoverLetter ===
                                                            false
                                                            ? 'selection-labeler drawn'
                                                            : 'selection-labeler'
                                                    }
                                                />
                                            </Tooltip>
                                        )}

                                        <DocumentRedact
                                            step={step}
                                            docNumber={documentLeftKey}
                                            onRefreshDocument={
                                                handleDocumentResetedByRefresh
                                            }
                                            inputRef={
                                                scrollLeftDocumentInputRef
                                            }
                                            ref={innerLeftDocumentRef}
                                            className="viewer"
                                            file={leftDocumentUri}
                                            onScrolling={(val) => {
                                                setScrollingLeftDocument(val);
                                            }}
                                            onLoadStart={() => {
                                                setDocumentLeftSuccess(false);
                                            }}
                                            onLoadCancelation={() => {
                                                // setDocumentSourceLeftFinishBounced(
                                                //     true
                                                // );
                                                setDocumentLeftSuccess(false);
                                            }}
                                            onLoadNetworkError={() => {
                                                setDocumentLeftError(true);
                                                setDocumentLeftNetworkError(
                                                    true
                                                );
                                            }}
                                            onLoadError={() => {
                                                if (
                                                    isLeftDocumentPrefetch ===
                                                    false
                                                ) {
                                                    setDocumentLeftError(true);
                                                }
                                            }}
                                            onSourceSuccess={() => {
                                                setDocumentLoaderLeft(true);
                                                setDocumentLeftSourceError(
                                                    false
                                                );
                                            }}
                                            onSourceError={() => {
                                                setDocumentLeftSourceError(
                                                    true
                                                );
                                            }}
                                            error={
                                                <Box className="align-loader">
                                                    {delayedLeft ||
                                                    isLoaderLeftNeedValidation ? (
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    ) : (
                                                        <Typography variant="h3">
                                                            {documentLeftError}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                            noData={
                                                <Box className="align-loader">
                                                    {(isSuccessLoadedLeft ===
                                                        false &&
                                                        isSourceErrorLeft ===
                                                            false &&
                                                        (leftDocumentUri !==
                                                            null ||
                                                            isLeftDocumentPrefetch)) ||
                                                    isDocumentSourceLeftFinishBounced ===
                                                        false ||
                                                    delayedLeft ? (
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    ) : (
                                                        <Typography variant="h3">
                                                            {documentLeftError}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                            loading={
                                                <Box className="align-loader">
                                                    <CircularProgress
                                                        className="mr-2 margin-loader"
                                                        size={54}
                                                        color="inherit"
                                                        title="progress loading"
                                                    />
                                                </Box>
                                            }
                                            onLoadSuccess={
                                                onDocumentLeftLoadSuccess
                                            }
                                        >
                                            {Array.from({
                                                length: numPagesLeft ?? 0,
                                            }).map((el, index) => (
                                                <PageRedact
                                                    key={`${index}-leftpage`}
                                                    loading={
                                                        <Box className="align-loader">
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        </Box>
                                                    }
                                                    error={
                                                        <Box className="align-loader">
                                                            <Typography variant="h3">
                                                                Document Page
                                                                Missing
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    onRemove={(i) =>
                                                        handleLeftVerticesRemove(
                                                            i
                                                        )
                                                    }
                                                    onErased={(i) =>
                                                        handleLeftDrawnDeletation(
                                                            i,
                                                            index
                                                        )
                                                    }
                                                    onRenderSuccess={() => {
                                                        if (!isLeftRendered) {
                                                            setPageLeftRenderer(
                                                                true
                                                            );
                                                        }
                                                    }}
                                                    onLoadSuccess={() => {
                                                        setLeftRendererArray(
                                                            (prev) => {
                                                                const updateArray =
                                                                    prev;
                                                                updateArray[
                                                                    index
                                                                ] = true;
                                                                return [
                                                                    ...updateArray,
                                                                ];
                                                            }
                                                        );
                                                    }}
                                                    onLoadError={() => {
                                                        setLeftRendererArray(
                                                            (prev) => {
                                                                const updateArray =
                                                                    prev;
                                                                updateArray[
                                                                    index
                                                                ] = false;
                                                                return [
                                                                    ...updateArray,
                                                                ];
                                                            }
                                                        );
                                                    }}
                                                    onChangeMeasure={
                                                        handleChangeMeasurement
                                                    }
                                                    verticesData={GetLeftVerticesDataWithMeasuredCanvas(
                                                        index + 1
                                                    )}
                                                    canDrawn={
                                                        isLeftAvailableDrawn
                                                    }
                                                    pageNumber={index + 1}
                                                    canvasRef={(
                                                        canvasElementRef
                                                    ) => {
                                                        if (
                                                            canvasElementRef !==
                                                            null
                                                        )
                                                            canvasLeftContentRef.current[
                                                                index
                                                            ] = canvasElementRef;
                                                    }}
                                                    canvasInnerRef={(
                                                        canvasElementRef
                                                    ) => {
                                                        if (
                                                            canvasElementRef !==
                                                            null
                                                        )
                                                            canvasInnerLeftContentRef.current[
                                                                index
                                                            ] = canvasElementRef;
                                                    }}
                                                    redactRef={(redactRef) => {
                                                        if (redactRef !== null)
                                                            redactLeftContentRef.current[
                                                                index
                                                            ] = redactRef;
                                                    }}
                                                    onDrawn={(
                                                        startpoint,
                                                        endpoint
                                                    ) =>
                                                        handleLeftDocumentOnDrawn(
                                                            startpoint,
                                                            endpoint,
                                                            index
                                                        )
                                                    }
                                                    width={400 + 25 * zoomLeft}
                                                    height={400 + 25 * zoomLeft}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={
                                                        false
                                                    }
                                                />
                                            ))}
                                        </DocumentRedact>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}

                        {isRightActive && (
                            <Grid
                                container
                                spacing={0}
                                className="document-grid right"
                            >
                                <Grid
                                    ref={innerRightTooltip}
                                    container
                                    item
                                    xs={12}
                                    spacing={0}
                                    className="document-tooltip"
                                    sx={{
                                        height:
                                            rightTooltipHeight !== 'auto'
                                                ? `${rightTooltipHeight} !important`
                                                : '80px',
                                    }}
                                >
                                    <Grid
                                        component={Paper}
                                        container
                                        item
                                        xs={12}
                                        spacing={0}
                                        elevation={24}
                                        className={
                                            rightTooltipHeight === 'auto'
                                                ? 'auto-size'
                                                : undefined
                                        }
                                        sx={{
                                            height:
                                                rightTooltipHeight !== 'auto'
                                                    ? rightTooltipHeight
                                                    : undefined,
                                        }}
                                    >
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="h5">
                                                Category :{' '}
                                                <strong>{rightCategory}</strong>
                                                {isRightOriginalMissing && (
                                                    <strong>
                                                        {' '}
                                                        (Document Missing)
                                                    </strong>
                                                )}
                                            </Typography>

                                            <Typography variant="h6">
                                                {rightFilename}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Stack direction="row" spacing={2}>
                                                <Box className="zoom-container">
                                                    <FormControl
                                                        fullWidth
                                                        variant="standard"
                                                        className="aggregator-zoom"
                                                    >
                                                        <InputLabel htmlFor="standard-adornment-amount">
                                                            Zoom
                                                        </InputLabel>
                                                        <Input
                                                            inputProps={{
                                                                'data-testid':
                                                                    'test-zoom-label',
                                                                role: 'spinbutton',
                                                                'aria-label':
                                                                    'zoom',
                                                                'aria-invalid':
                                                                    zoomRight <
                                                                        0 ||
                                                                    zoomRight >
                                                                        100
                                                                        ? 'true'
                                                                        : 'false',
                                                                'aria-valuenow':
                                                                    zoomRight,
                                                                'aria-valuemax': 100,
                                                                'aria-valuemin': 0,
                                                            }}
                                                            disableUnderline
                                                            value={`${zoomRight}%`}
                                                            disabled
                                                            id="standard-adornment-amount"
                                                            startAdornment={
                                                                <InputAdornment position="start">
                                                                    <IconButton
                                                                        disabled={
                                                                            !isSuccessLoadedRight
                                                                        }
                                                                        title="zoom out"
                                                                        onClick={
                                                                            onHandleZoomRightOut
                                                                        }
                                                                        //   onMouseDown={handleMouseDownPassword}
                                                                        edge="end"
                                                                    >
                                                                        <ZoomOut />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            }
                                                            endAdornment={
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        disabled={
                                                                            !isSuccessLoadedRight
                                                                        }
                                                                        title="zoom in"
                                                                        onClick={
                                                                            onHandleZoomRightIn
                                                                        }
                                                                        //   onMouseDown={handleMouseDownPassword}
                                                                        edge="end"
                                                                    >
                                                                        <ZoomIn />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            }
                                                        />
                                                    </FormControl>
                                                </Box>
                                                <IconButton
                                                    className="tooltip-button"
                                                    aria-label="fullscreen"
                                                    disabled={
                                                        !isSuccessLoadedRight
                                                    }
                                                    onClick={
                                                        actionFullScreenForRightViewer
                                                    }
                                                >
                                                    {fullScreenModule ===
                                                    'right' ? (
                                                        <CloseFullscreenIcon />
                                                    ) : (
                                                        <OpenInFullIcon />
                                                    )}
                                                </IconButton>
                                                <IconButton
                                                    className="tooltip-button hidden"
                                                    aria-label="print"
                                                    disabled={
                                                        !isSuccessLoadedRight
                                                    }
                                                    onClick={
                                                        actionTriggerHandleRightDocument
                                                    }
                                                >
                                                    <LocalPrintshopOutlinedIcon />
                                                </IconButton>
                                                <IconButton
                                                    className="tooltip-button"
                                                    aria-label="download"
                                                    disabled={
                                                        !isSuccessLoadedRight
                                                    }
                                                    onClick={
                                                        actionDownloadForRightTooltip
                                                    }
                                                >
                                                    {isDownloadRightDocument ? (
                                                        <DownloadingOutlinedIcon />
                                                    ) : (
                                                        <FileDownloadOutlinedIcon />
                                                    )}
                                                </IconButton>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    item
                                    xs={12}
                                    spacing={0}
                                    className="document-viewer"
                                >
                                    <Grid
                                        item
                                        xs={rightViewOpen ? 12 : 0}
                                        md={rightViewOpen ? 3 : 0}
                                        className="sideview"
                                        sx={handleRenderOffsetForSideView}
                                    >
                                        <IconButton
                                            onClick={onHandleRightSidewayToggle}
                                            className={
                                                rightViewOpen
                                                    ? 'tooltip-button'
                                                    : 'tooltip-button close-button'
                                            }
                                            aria-label="show sideview"
                                        >
                                            {matches && rightViewOpen && (
                                                <KeyboardDoubleArrowRightIcon />
                                            )}
                                            {matches && !rightViewOpen && (
                                                <KeyboardDoubleArrowLeftIcon />
                                            )}
                                            {!matches && rightViewOpen && (
                                                <KeyboardDoubleArrowUpIcon />
                                            )}
                                            {!matches && !rightViewOpen && (
                                                <KeyboardDoubleArrowDownIcon />
                                            )}
                                        </IconButton>
                                        {rightViewOpen && (
                                            <Document
                                                ref={outerRightDocumentRef}
                                                className="viewer"
                                                file={rightDocumentUri}
                                                onLoadSuccess={
                                                    onDocumentRightLoadSuccess
                                                }
                                                error={
                                                    <Box className="align-loader">
                                                        {delayedRight ? (
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        ) : (
                                                            <Typography variant="h3">
                                                                {
                                                                    documentRightError
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                noData={
                                                    <Box className="align-loader">
                                                        {isSuccessLoadedRight ===
                                                            false ||
                                                        delayedRight ? (
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        ) : (
                                                            <Typography variant="h3">
                                                                {
                                                                    documentRightError
                                                                }
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                loading={
                                                    <Box className="align-loader">
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    </Box>
                                                }
                                            >
                                                {Array.from({
                                                    length: numPagesRight ?? 0,
                                                }).map((el, index) => (
                                                    <Page
                                                        key={`sideviewright-${index}`}
                                                        onClick={
                                                            onHandleRightSideview
                                                        }
                                                        pageNumber={index + 1}
                                                        width={100}
                                                        height={100}
                                                        renderTextLayer={false}
                                                        renderAnnotationLayer={
                                                            false
                                                        }
                                                        loading={
                                                            <Box className="align-loader">
                                                                <CircularProgress
                                                                    className="mr-2 margin-loader"
                                                                    size={54}
                                                                    color="inherit"
                                                                    title="progress loading"
                                                                />
                                                            </Box>
                                                        }
                                                        error={
                                                            <Box className="align-loader">
                                                                <Typography variant="h3">
                                                                    Document
                                                                    Page Missing
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                ))}
                                            </Document>
                                        )}
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={rightViewOpen ? 9 : 12}
                                        className="contentview"
                                        sx={handleRenderOffsetForContentView}
                                    >
                                        {isSuccessLoadedRight && (
                                            <Tooltip
                                                title={
                                                    GenerateRightTitleForDrawnTooltip
                                                }
                                                placement="left-start"
                                            >
                                                <CropFreeIcon
                                                    sx={{
                                                        top: `${rightTooltipHint} !important`,
                                                    }}
                                                    className={
                                                        ((Array.isArray(
                                                            rightVerticesData
                                                        ) &&
                                                            rightVerticesData.length ===
                                                                0) ||
                                                            rightVerticesData ===
                                                                null) &&
                                                        isRightDrawn ===
                                                            false &&
                                                        isRightCoverLetter ===
                                                            false
                                                            ? 'selection-labeler drawn'
                                                            : 'selection-labeler'
                                                    }
                                                />
                                            </Tooltip>
                                        )}

                                        <DocumentRedact
                                            step={step}
                                            docNumber={documentRightKey}
                                            onRefreshDocument={
                                                handleDocumentResetedByRefresh
                                            }
                                            inputRef={
                                                scrollRightDocumentInputRef
                                            }
                                            ref={innerRightDocumentRef}
                                            className="viewer"
                                            onScrolling={(val) => {
                                                setScrollingRightDocument(val);
                                            }}
                                            file={rightDocumentUri}
                                            onLoadStart={() => {
                                                setDocumentRightSuccess(false);
                                            }}
                                            onLoadCancelation={() => {
                                                // setDocumentSourceRightFinishBounced(
                                                //     true
                                                // );
                                                setDocumentRightSuccess(false);
                                            }}
                                            onLoadNetworkError={() => {
                                                setDocumentRightError(true);
                                                setDocumentRightNetworkError(
                                                    true
                                                );
                                            }}
                                            onLoadError={() => {
                                                if (
                                                    isRightDocumentPrefetch ===
                                                    false
                                                ) {
                                                    setDocumentRightError(true);
                                                }
                                            }}
                                            onSourceSuccess={() => {
                                                setDocumentLoaderRight(true);
                                                setDocumentRightSourceError(
                                                    false
                                                );
                                            }}
                                            onSourceError={() => {
                                                setDocumentRightSourceError(
                                                    true
                                                );
                                            }}
                                            error={
                                                <Box className="align-loader">
                                                    {delayedRight ||
                                                    isLoaderRightNeedValidation ? (
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    ) : (
                                                        <Typography variant="h3">
                                                            {documentRightError}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                            onLoadSuccess={
                                                onDocumentRightLoadSuccess
                                            }
                                            noData={
                                                <Box className="align-loader">
                                                    {(isSuccessLoadedRight ===
                                                        false &&
                                                        isSourceErrorRight ===
                                                            false &&
                                                        (rightDocumentUri !==
                                                            null ||
                                                            isRightDocumentPrefetch)) ||
                                                    isDocumentSourceRightFinishBounced ===
                                                        false ||
                                                    delayedRight ? (
                                                        <CircularProgress
                                                            className="mr-2 margin-loader"
                                                            size={54}
                                                            color="inherit"
                                                            title="progress loading"
                                                        />
                                                    ) : (
                                                        <Typography variant="h3">
                                                            {documentRightError}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                            loading={
                                                <Box className="align-loader">
                                                    <CircularProgress
                                                        className="mr-2 margin-loader"
                                                        size={54}
                                                        color="inherit"
                                                        title="progress loading"
                                                    />
                                                </Box>
                                            }
                                        >
                                            {Array.from({
                                                length: numPagesRight ?? 0,
                                            }).map((el, index) => (
                                                <PageRedact
                                                    key={`${index}-rightpage`}
                                                    loading={
                                                        <Box className="align-loader">
                                                            <CircularProgress
                                                                className="mr-2 margin-loader"
                                                                size={54}
                                                                color="inherit"
                                                                title="progress loading"
                                                            />
                                                        </Box>
                                                    }
                                                    error={
                                                        <Box className="align-loader">
                                                            <Typography variant="h3">
                                                                Document Page
                                                                Missing
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    onRemove={(i) =>
                                                        handleRightVerticesRemove(
                                                            i
                                                        )
                                                    }
                                                    onErased={(i) =>
                                                        handleRightDrawnDeletation(
                                                            i,
                                                            index
                                                        )
                                                    }
                                                    onRenderSuccess={() => {
                                                        if (!isRightRendered) {
                                                            setPageRightRenderer(
                                                                true
                                                            );
                                                        }
                                                    }}
                                                    onLoadSuccess={() => {
                                                        setRightRendererArray(
                                                            (prev) => {
                                                                const updateArray =
                                                                    prev;
                                                                updateArray[
                                                                    index
                                                                ] = true;
                                                                return [
                                                                    ...updateArray,
                                                                ];
                                                            }
                                                        );
                                                    }}
                                                    onLoadError={() => {
                                                        setRightRendererArray(
                                                            (prev) => {
                                                                const updateArray =
                                                                    prev;
                                                                updateArray[
                                                                    index
                                                                ] = false;
                                                                return [
                                                                    ...updateArray,
                                                                ];
                                                            }
                                                        );
                                                    }}
                                                    onChangeMeasure={
                                                        handleChangeMeasurement
                                                    }
                                                    verticesData={GetRightVerticesDataWithMeasuredCanvas(
                                                        index + 1
                                                    )}
                                                    pageNumber={index + 1}
                                                    canvasRef={(
                                                        canvasElementRef
                                                    ) => {
                                                        if (
                                                            canvasElementRef !==
                                                            null
                                                        )
                                                            canvasRightContentRef.current[
                                                                index
                                                            ] = canvasElementRef;
                                                    }}
                                                    canvasInnerRef={(
                                                        canvasElementRef
                                                    ) => {
                                                        if (
                                                            canvasElementRef !==
                                                            null
                                                        )
                                                            canvasInnerRightContentRef.current[
                                                                index
                                                            ] = canvasElementRef;
                                                    }}
                                                    redactRef={(redactRef) => {
                                                        if (redactRef !== null)
                                                            redactRightContentRef.current[
                                                                index
                                                            ] = redactRef;
                                                    }}
                                                    canDrawn={
                                                        isRightAvailableDrawn
                                                    }
                                                    onDrawn={(
                                                        startpoint,
                                                        endpoint
                                                    ) =>
                                                        handleRightDocumentOnDrawn(
                                                            startpoint,
                                                            endpoint,
                                                            index
                                                        )
                                                    }
                                                    width={400 + 25 * zoomRight}
                                                    height={
                                                        400 + 25 * zoomRight
                                                    }
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={
                                                        false
                                                    }
                                                />
                                            ))}
                                        </DocumentRedact>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Stack>
                </Box>
            </FullScreen>
        );
    }
);
export default DocumentViewer;
